import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { createPortal } from "react-dom";
import { useParams, useLocation } from "react-router-dom";
import { Box, Flex, Text } from "rebass/styled-components";
import { useRecoilValue } from "recoil";
import { useProEditAccess } from "MVVM/Hooks/useProEditAccess";
import { AllstarUser } from "MVVM/State";
import { analytics } from "MVVM/Analytics";
import { usePortal } from "MVVM/Hooks/usePortal";
import { ClipEligibilityModal } from "MVVM/Components/Modals/ClipEligibilityModal";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";
import { isAtClipLimit } from "MVVM/Utilities/ClipLimits";
import {
  FaSkull,
  FaCrosshairs,
  FaPlus,
  FaMinus,
  FaTrash,
  FaUser,
  FaVideo,
  FaChevronUp,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaLayerGroup,
  FaDiscord,
  FaQuestionCircle,
  FaExclamationTriangle,
  FaMinusCircle,
  FaCheck,
} from "react-icons/fa";
import { MdMovieEdit } from "react-icons/md";

import { TimelineEditorViewModel as useTimelineEditorViewModel } from "./ViewModel";
import { getMapThumbnail } from "../../Utilities/Clip";
import { ABrandSymbol } from "../../Components/Logo";
import {
  ISelection,
  IKillTiming,
  IRoundData,
  IKillEvent,
  IBombEvent,
  IPlayer,
  IReplayCommand,
  ITimelineEditorProps,
  TMountedCameraType,
  ISuggestedMoment,
} from "./types";
import {
  BUFFER_TICKS,
  CAMERA_LEAD_TICKS,
  MOUNTED_CAMERA_OPTIONS,
} from "./constants";
import { getWeaponIconUrl, detectSuggestedMoments } from "./utils";
import {
  PlayerSelectorWizard,
  SuggestedMomentsPanel,
  TitleModal,
  PlayerPickerModal,
} from "./Components";
import {
  Container,
  HeaderWrapper,
  Header,
  MainContent,
  TimelinePanel,
  ControlBar,
  MinimapContainer,
  MinimapLabel,
  RoundIndicator,
  RoundPip,
  HalftimeDivider,
  ZoomButton,
  WeaponSelect,
  ToggleLabel,
  ToggleCheckbox,
  ToggleSlider,
  TimelineContainerWrapper,
  TimelineContainer,
  CustomScrollbarContainer,
  CustomScrollbarTrack,
  CustomScrollbarThumb,
  TimelineInner,
  TimelineRuler,
  RulerHeader,
  RoundsHeader,
  PlayerColumnHeader,
  RulerRow,
  RulerMark,
  RoundsRow,
  RoundBlock,
  RoundLabel,
  RoundScore,
  PlayersContainer,
  TeamDivider,
  TeamDividerLine,
  PlayerRow,
  PlayerInfo,
  PlayerAvatar,
  PlayerAvatarImage,
  PlayerDetails,
  PlayerRemoveHint,
  PlayerName,
  YouBadge,
  PlayerStats,
  StatBadge,
  PlayerTimeline,
  RoundSegment,
  KillMarker,
  DeathMarker,
  SelectionOverlay,
  JumpCutSegmentOverlay,
  PreviewOverlay,
  SelectionHandle,
  SelectionsPanel,
  PanelHeader,
  PanelHeaderTop,
  CollapseButton,
  Toast,
  SelectionsList,
  EmptyState,
  EmptyIcon,
  SelectionCard,
  ClipHeader,
  SegmentTabs,
  SegmentTab,
  SegmentTabContent,
  SegmentDoneButton,
  SegmentExpandHint,
  TabInstruction,
  DurationSlider,
  DurationTrack,
  DurationFill,
  DurationJumpCutSegment,
  DurationKillMarker,
  DurationLabels,
  DurationTimingLabels,
  KillsList,
  KillItem,
  KillItemHeader,
  KillWeaponIcon,
  CameraOptionsSection,
  CameraKillItem,
  CameraKillInfo,
  CameraKillNumber,
  CameraKillPlayers,
  CameraPovSwitch,
  CameraPovBtn,
  CameraIconButton,
  CameraPopover,
  CameraPopoverOption,
  TimeAdjustRow,
  TimeAdjustGroup,
  TimeLabel,
  TimeInputWrapper,
  TimeIncrementBtn,
  TimeInput,
  JumpCutsSection,
  JumpCutsToggle,
  JumpCutsLabel,
  JumpCutsDescription,
  JumpCutsKillList,
  JumpCutsKillItem,
  JumpCutsKillNumber,
  JumpCutsKillInfo,
  JumpCutsKillPlayer,
  JumpCutsKillWeapon,
  JumpCutsTimingControls,
  JumpCutsTimingGroup,
  JumpCutsTimingLabel,
  JumpCutsTimingValue,
  JumpCutsTimingBtn,
  JumpCutsTimingDisplay,
  ClipNumber,
  DeleteButton,
  PanelFooter,
  TotalDuration,
  CreateButton,
  Tooltip,
  TooltipTitle,
  TooltipRow,
  TooltipAction,
  TooltipMerge,
  LoadingContainer,
  LoadingSpinner,
  LoadingContent,
  LoadingTitle,
  LoadingSubtitle,
  WelcomeModalOverlay,
  WelcomeModalContent,
  WelcomeModalTitle,
  WelcomeModalSubtitle,
  WelcomeModalSection,
  WelcomeModalSectionTitle,
  WelcomeModalList,
  WelcomeModalListItem,
  WelcomeModalListNumber,
  WelcomeModalShortcuts,
  WelcomeModalShortcutItem,
  WelcomeModalDiscord,
  WelcomeModalButton,
  WelcomeModalKeyboard,
  WelcomeModalWarning,
  WelcomeScreen,
  WelcomeCard,
  WelcomeIcon,
  WelcomeTitle,
  WelcomeSubtitle,
  WelcomeSelectButton,
} from "./styledComponents";

const TimelineEditor = ({
  data,
  onGenerateSequence,
  onSubmitSequence,
  isSubmitting,
}: ITimelineEditorProps) => {
  const { matchId } = useParams<{ matchId: string }>();

  if (!data || !data.header) {
    return null;
  }

  const TICK_RATE = data.header.tickRate;
  const TOTAL_TICKS = data.header.totalTicks;
  const ROUNDS = data.rounds as IRoundData[];
  const PLAYERS = data.players as IPlayer[];

  // LocalStorage key for this match's segments
  const STORAGE_KEY = `timelineEditorSegments_${matchId}`;

  const tickToTime = useCallback(
    (tick: number): string => {
      const seconds = tick / TICK_RATE;
      const minutes = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${minutes}:${secs.toString().padStart(2, "0")}`;
    },
    [TICK_RATE],
  );

  const getPlayerName = useCallback(
    (steamId: string): string => {
      const player = PLAYERS.find((p) => p.steamId === steamId);
      if (!player) return "Unknown";
      return player.name.replace(/^[⑧⑨⑩⑪⑫⑬]\s*[✓★☆]*\s*/, "").trim();
    },
    [PLAYERS],
  );

  // Calculate effective duration of a selection (accounting for jump cuts)
  const getSelectionDuration = useCallback((selection: ISelection): number => {
    if (
      selection.useJumpCuts &&
      selection.killTimings &&
      selection.killTimings.length > 0
    ) {
      // Sum up the duration of each kill segment
      return selection.killTimings.reduce((acc, kt) => {
        const segmentStart = Math.max(
          selection.startTick,
          kt.tick - kt.leadUpTicks,
        );
        const segmentEnd = Math.min(
          selection.endTick,
          kt.tick + kt.aftermathTicks,
        );
        return acc + (segmentEnd - segmentStart);
      }, 0);
    }
    // Normal duration
    return selection.endTick - selection.startTick;
  }, []);

  const getKillTeams = useCallback(
    (
      kill: IKillEvent,
    ): { killerTeam: "ct" | "t" | null; victimTeam: "ct" | "t" | null } => {
      const isCT = (team: string) =>
        team === "CounterTerrorists" ||
        team === "CT" ||
        team?.toLowerCase().includes("counter");

      const killerIsCT = kill.killerData?.team
        ? isCT(kill.killerData.team)
        : null;

      if (killerIsCT === null) {
        return { killerTeam: null, victimTeam: null };
      }

      return {
        killerTeam: killerIsCT ? "ct" : "t",
        victimTeam: killerIsCT ? "t" : "ct",
      };
    },
    [],
  );

  const getPlayerObserverSlot = useCallback(
    (steamId: string): number => {
      const player = PLAYERS.find((p) => p.steamId === steamId);
      return player?.observerSlot || 0;
    },
    [PLAYERS],
  );

  const getKillsInRange = useCallback(
    (startTick: number, endTick: number, playerId?: string): IKillEvent[] => {
      const kills: IKillEvent[] = [];
      ROUNDS.forEach((round) => {
        round.kills?.forEach((kill) => {
          if (kill.tick >= startTick && kill.tick <= endTick) {
            if (!playerId || kill.killer === playerId) {
              kills.push(kill);
            }
          }
        });
      });
      return kills;
    },
    [ROUNDS],
  );

  const timeToTick = useCallback(
    (timeStr: string): number | null => {
      const match = timeStr.match(/^(\d+):(\d{2})$/);
      if (!match) return null;
      const minutes = parseInt(match[1], 10);
      const seconds = parseInt(match[2], 10);
      if (seconds >= 60) return null;
      return (minutes * 60 + seconds) * TICK_RATE;
    },
    [TICK_RATE],
  );

  // Compute each player's team in the first half (rounds 1-12) once
  const playerFirstHalfTeams = useMemo(() => {
    const teams: Record<string, "ct" | "t" | null> = {};

    const isCT = (team: string) =>
      team === "CounterTerrorists" ||
      team === "CT" ||
      team?.toLowerCase().includes("counter");

    const getTeamFromKill = (
      kill: IKillEvent,
      playerId: string,
    ): "ct" | "t" => {
      const killerIsCT = isCT(kill.killerData?.team);
      if (kill.killer === playerId) {
        return killerIsCT ? "ct" : "t";
      }
      return killerIsCT ? "t" : "ct";
    };

    PLAYERS.forEach((player) => {
      const steamId = player.steamId;

      // First, search first half rounds (1-12)
      for (const round of ROUNDS) {
        if (round.round === 999 || round.round > 12) continue;
        const kill = round.kills?.find(
          (k) =>
            (k.killer === steamId || k.victim === steamId) &&
            k.killer !== "0" &&
            k.victim !== "0",
        );
        if (kill) {
          teams[steamId] = getTeamFromKill(kill, steamId);
          break;
        }
      }

      // If not found in first half, search second half and swap
      if (!teams[steamId]) {
        for (const round of ROUNDS) {
          if (round.round === 999 || round.round <= 12) continue;
          const kill = round.kills?.find(
            (k) =>
              (k.killer === steamId || k.victim === steamId) &&
              k.killer !== "0" &&
              k.victim !== "0",
          );
          if (kill) {
            const secondHalfTeam = getTeamFromKill(kill, steamId);
            // Swap since this is second half
            teams[steamId] = secondHalfTeam === "ct" ? "t" : "ct";
            break;
          }
        }
      }

      if (!teams[steamId]) {
        teams[steamId] = null;
      }
    });

    return teams;
  }, [PLAYERS, ROUNDS]);

  const getPlayerTeamInRound = useCallback(
    (steamId: string, round: IRoundData): "ct" | "t" | null => {
      const firstHalfTeam = playerFirstHalfTeams[steamId];
      if (!firstHalfTeam) return null;

      // In first half (rounds 1-12), use the first half team
      // In second half (rounds 13+), swap the team
      if (round.round <= 12) {
        return firstHalfTeam;
      } else {
        return firstHalfTeam === "ct" ? "t" : "ct";
      }
    },
    [playerFirstHalfTeams],
  );

  const [zoom, setZoom] = useState(2);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [selections, setSelections] = useState<ISelection[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch {
      // Failed to load from localStorage, start fresh
    }
    return [];
  });
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<number | null>(null);
  const [currentSelection, setCurrentSelection] = useState<{
    startTick: number;
    endTick: number;
    playerId: string;
  } | null>(null);
  const [resizingSelection, setResizingSelection] = useState<{
    id: string;
    edge: "left" | "right";
    originalTick: number;
  } | null>(null);
  const [selectedPlayers, setSelectedPlayers] = useState<Set<string>>(
    new Set(),
  );
  const [showPlayerPicker, setShowPlayerPicker] = useState<boolean>(false);
  const [hoveredEvent, setHoveredEvent] = useState<{
    type: string;
    data: IKillEvent | IBombEvent;
    x: number;
    y: number;
  } | null>(null);
  const [hoveredKillPreview, setHoveredKillPreview] = useState<{
    startTick: number;
    endTick: number;
    playerId: string;
    wouldMergeWith: string | null;
  } | null>(null);
  const [defaultLeadUp, setDefaultLeadUp] = useState(5);
  const [defaultAftermath, setDefaultAftermath] = useState(5);
  const [toast, setToast] = useState<string | null>(null);
  const [weaponFilter, setWeaponFilter] = useState<string>("all");
  const [killPovPrefs, setKillPovPrefs] = useState<
    Record<string, "killer" | "victim">
  >({});
  const [mountedCameras, setMountedCameras] = useState<
    Record<string, TMountedCameraType>
  >({});
  const [openCameraPopover, setOpenCameraPopover] = useState<string | null>(
    null,
  );
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [clipTitle, setClipTitle] = useState<string>("");
  const [cooldownEndTime, setCooldownEndTime] = useState<number | null>(null);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
  const [showDeaths, setShowDeaths] = useState<boolean>(false);
  const [panelCollapsed, setPanelCollapsed] = useState<boolean>(true);
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null,
  );
  const [selectionsHistory, setSelectionsHistory] = useState<ISelection[][]>(
    [],
  );
  const [showWelcomeModal, setShowWelcomeModal] = useState<boolean>(() => {
    const hasSeenWelcome = localStorage.getItem("timelineEditorWelcomeSeen");
    return !hasSeenWelcome;
  });
  const [excludedMoments, setExcludedMoments] = useState<
    Record<string, Set<string>>
  >({});
  const [showTitleModal, setShowTitleModal] = useState<boolean>(false);
  const [clipSubmitted, setClipSubmitted] = useState<boolean>(false);
  const [profileButtonCountdown, setProfileButtonCountdown] =
    useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [recentlyAddedId, setRecentlyAddedId] = useState<string | null>(null);
  const [isDraggingScrollbar, setIsDraggingScrollbar] =
    useState<boolean>(false);
  const [suggestionsCollapsed, setSuggestionsCollapsed] =
    useState<boolean>(false);
  const [addedMomentIds, setAddedMomentIds] = useState<Set<string>>(new Set());
  const [showDebugModal, setShowDebugModal] = useState<boolean>(false);
  const [debugSequence, setDebugSequence] = useState<IReplayCommand[]>([]);

  const location = useLocation();
  const isDebugMode = new URLSearchParams(location.search).has("debug");

  // Maps selection IDs to moment IDs for tracking when segments are deleted
  const [selectionToMomentMap, setSelectionToMomentMap] = useState<
    Map<string, string>
  >(new Map());

  // Tab state for segment cards (overview, timing, camera)
  const [segmentTabs, setSegmentTabs] = useState<
    Record<string, "overview" | "timing" | "camera">
  >({});

  const getSegmentTab = (segmentId: string) =>
    segmentTabs[segmentId] || "overview";
  const setSegmentTab = (
    segmentId: string,
    tab: "overview" | "timing" | "camera",
  ) => {
    setSegmentTabs((prev) => ({ ...prev, [segmentId]: tab }));
  };

  // Track which segment is expanded (only one at a time)
  const [expandedSegmentId, setExpandedSegmentId] = useState<string | null>(
    null,
  );

  // Track which player is focused for showing their highlights
  const [highlightFocusPlayerId, setHighlightFocusPlayerId] = useState<
    string | null
  >(null);

  // Wizard step state for onboarding flow - start in editor mode, user adds players manually
  const [wizardStep, setWizardStep] = useState<"player" | "moments" | "editor">(
    "editor",
  );
  const [wizardSelectedPlayer, setWizardSelectedPlayer] = useState<
    string | null
  >(null);

  const allstarUser = useRecoilValue(AllstarUser);
  const { hasProEditAccess } = useProEditAccess();
  const showOverhaul = useShouldShowSubscriptionOverhaul();
  const currentUserSteamId = allstarUser?.user?.steam?.steamid || null;

  const {
    portalOpen: subscriptionPaywallOpen,
    setPortalOpen: setSubscriptionPaywallOpen,
    Portal: SubscriptionPaywallPortal,
  } = usePortal(
    <UnifiedUpgradeModal
      featureName="Timeline Editor"
      upgradeAction="Use the Timeline Editor"
      requiredTierKey="ESSENTIALS"
      analyticsSource="Timeline Editor"
      onClose={() => setSubscriptionPaywallOpen(false)}
    />,
  );

  const {
    portalOpen: eligibilityModalOpen,
    setPortalOpen: setEligibilityModalOpen,
    Portal: EligibilityModalPortal,
  } = usePortal(
    <ClipEligibilityModal
      clipQuota={allstarUser?.user?.clipQuota}
      userGroup={allstarUser?.user?.userGroup?.type}
      isCs2Clip
      onClose={() => setEligibilityModalOpen(false)}
    />,
  );

  const COOLDOWN_SECONDS = 60;

  const timelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectionsListRef = useRef<HTMLDivElement>(null);
  const prevSelectionsLengthRef = useRef<number>(0);
  const scrollbarTrackRef = useRef<HTMLDivElement>(null);
  const scrollbarDragStartRef = useRef<{
    x: number;
    scrollLeft: number;
  } | null>(null);
  const selectionCardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const validRounds = useMemo(
    () => ROUNDS.filter((r) => r.round !== 999),
    [ROUNDS],
  );

  const uniqueWeapons = useMemo(() => {
    const weapons = new Set<string>();
    validRounds.forEach((round) => {
      round.kills?.forEach((kill) => {
        if (kill.killerData?.weapon) {
          weapons.add(kill.killerData.weapon);
        }
      });
    });
    return Array.from(weapons).sort();
  }, [validRounds]);

  // Detect multi-kills and aces for suggestions panel
  const suggestedMoments = useMemo(() => {
    return detectSuggestedMoments(validRounds, PLAYERS, TICK_RATE);
  }, [validRounds, PLAYERS, TICK_RATE]);

  const firstRoundStart = validRounds[0]?.startTick || 0;
  const lastRoundEnd =
    validRounds[validRounds.length - 1]?.endTick || TOTAL_TICKS;
  const matchDuration = lastRoundEnd - firstRoundStart;

  const baseWidth = 2000;
  const timelineWidth = baseWidth * zoom;

  const playerStats = useMemo(() => {
    const stats: Record<string, { kills: number; deaths: number }> = {};
    PLAYERS.forEach((p) => {
      stats[p.steamId] = { kills: 0, deaths: 0 };
    });
    validRounds.forEach((round) => {
      round.kills?.forEach((kill) => {
        if (stats[kill.killer]) stats[kill.killer].kills++;
        if (stats[kill.victim]) stats[kill.victim].deaths++;
      });
    });
    return stats;
  }, [PLAYERS, validRounds]);

  const { sortedPlayers, ctPlayerCount } = useMemo(() => {
    const getPlayerTeam = (steamId: string): "ct" | "t" | null => {
      for (const round of validRounds) {
        const kill = round.kills?.find(
          (k) =>
            (k.killer === steamId || k.victim === steamId) &&
            k.killer !== "0" &&
            k.victim !== "0",
        );
        if (kill) {
          const isCT = (team: string) =>
            team === "CounterTerrorists" ||
            team === "CT" ||
            team?.toLowerCase().includes("counter");
          const killerIsCT = isCT(kill.killerData?.team);
          if (kill.killer === steamId) {
            return killerIsCT ? "ct" : "t";
          }
          return killerIsCT ? "t" : "ct";
        }
      }
      return null;
    };

    const ctPlayers: IPlayer[] = [];
    const tPlayers: IPlayer[] = [];
    const unknownPlayers: IPlayer[] = [];

    PLAYERS.forEach((player) => {
      const team = getPlayerTeam(player.steamId);
      if (team === "ct") {
        ctPlayers.push(player);
      } else if (team === "t") {
        tPlayers.push(player);
      } else {
        unknownPlayers.push(player);
      }
    });

    return {
      sortedPlayers: [...ctPlayers, ...tPlayers, ...unknownPlayers],
      ctPlayerCount: ctPlayers.length,
      tPlayerCount: tPlayers.length,
    };
  }, [PLAYERS, validRounds]);

  const sortedSelections = useMemo(
    () => [...selections].sort((a, b) => a.startTick - b.startTick),
    [selections],
  );

  // Save selections to localStorage when they change
  useEffect(() => {
    try {
      if (selections.length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selections));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Failed to save to localStorage
    }
  }, [selections, STORAGE_KEY]);

  // Track container width for viewport calculations
  useEffect(() => {
    if (!containerRef.current) return;

    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openCameraPopover) {
        const target = e.target as HTMLElement;
        if (!target.closest("[data-camera-popover]")) {
          setOpenCameraPopover(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openCameraPopover]);

  const tickToPixel = useCallback(
    (tick: number): number => {
      const relativeTick = tick - firstRoundStart;
      return (relativeTick / matchDuration) * timelineWidth;
    },
    [firstRoundStart, matchDuration, timelineWidth],
  );

  const pixelToTick = useCallback(
    (pixel: number): number => {
      const relativeTick = (pixel / timelineWidth) * matchDuration;
      return Math.round(relativeTick + firstRoundStart);
    },
    [firstRoundStart, matchDuration, timelineWidth],
  );

  // Calculate which rounds are visible in the viewport
  const visibleRoundNumbers = useMemo(() => {
    if (containerWidth === 0) return new Set<number>();

    const viewportStartPx = scrollPosition;
    const viewportEndPx = scrollPosition + containerWidth;

    const visible = new Set<number>();

    validRounds.forEach((round) => {
      const roundStartPx = tickToPixel(round.startTick);
      const roundEndPx = tickToPixel(round.endTick);

      // Check if round overlaps with viewport
      if (roundEndPx >= viewportStartPx && roundStartPx <= viewportEndPx) {
        visible.add(round.round);
      }
    });

    return visible;
  }, [scrollPosition, containerWidth, validRounds, tickToPixel]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Countdown for profile button after clip submission
  useEffect(() => {
    if (clipSubmitted) {
      setProfileButtonCountdown(5);
      const interval = setInterval(() => {
        setProfileButtonCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [clipSubmitted]);

  // Auto-scroll to new selection in sidebar when added
  useEffect(() => {
    if (selections.length > prevSelectionsLengthRef.current) {
      // A new selection was added, scroll to bottom of list
      const newSelection = selections[selections.length - 1];
      if (newSelection) {
        setRecentlyAddedId(newSelection.id);
        setExpandedSegmentId(newSelection.id);
        setSegmentTab(newSelection.id, "overview");
        // Clear the highlight after animation completes
        const timer = setTimeout(() => setRecentlyAddedId(null), 2000);

        if (selectionsListRef.current) {
          selectionsListRef.current.scrollTo({
            top: selectionsListRef.current.scrollHeight,
            behavior: "smooth",
          });
        }

        prevSelectionsLengthRef.current = selections.length;
        return () => clearTimeout(timer);
      }
    }
    prevSelectionsLengthRef.current = selections.length;
  }, [selections]);

  useEffect(() => {
    if (!cooldownEndTime) {
      setCooldownRemaining(0);
      return;
    }

    const updateCooldown = () => {
      const remaining = Math.ceil((cooldownEndTime - Date.now()) / 1000);
      if (remaining <= 0) {
        setCooldownEndTime(null);
        setCooldownRemaining(0);
      } else {
        setCooldownRemaining(remaining);
      }
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [cooldownEndTime]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Delete key - delete selected segment
      if ((e.key === "Delete" || e.key === "Backspace") && selectedSegmentId) {
        e.preventDefault();
        setSelectionsHistory((prev) => [...prev, selections]);
        setSelections((prev) => prev.filter((s) => s.id !== selectedSegmentId));
        // If this selection came from a suggested moment, uncheck it
        const momentId = selectionToMomentMap.get(selectedSegmentId);
        if (momentId) {
          setAddedMomentIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(momentId);
            return newSet;
          });
          setSelectionToMomentMap((prevMap) => {
            const newMap = new Map(prevMap);
            newMap.delete(selectedSegmentId);
            return newMap;
          });
        }
        setSelectedSegmentId(null);
      }

      // Ctrl+Z or Cmd+Z - undo
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        if (selectionsHistory.length > 0) {
          const previousState = selectionsHistory[selectionsHistory.length - 1];
          setSelectionsHistory((prev) => prev.slice(0, -1));
          setSelections(previousState);
          setSelectedSegmentId(null);
        }
      }

      // Escape - deselect segment
      if (e.key === "Escape") {
        setSelectedSegmentId(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedSegmentId, selections, selectionsHistory, selectionToMomentMap]);

  const PLAYER_INFO_WIDTH = 200;

  const handleTimelineMouseDown = (e: React.MouseEvent, playerId: string) => {
    if (!containerRef.current || !playerId) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const x =
      e.clientX - containerRect.left + scrollPosition - PLAYER_INFO_WIDTH;
    const tick = Math.max(
      firstRoundStart,
      Math.min(lastRoundEnd, pixelToTick(x)),
    );

    setIsSelecting(true);
    setSelectionStart(tick);
    setCurrentSelection({
      startTick: tick,
      endTick: tick,
      playerId,
    });
  };

  const handleTimelineMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const x =
      e.clientX - containerRect.left + scrollPosition - PLAYER_INFO_WIDTH;
    const tick = Math.max(
      firstRoundStart,
      Math.min(lastRoundEnd, pixelToTick(x)),
    );

    // Handle resizing existing selection
    if (resizingSelection) {
      const selection = selections.find((s) => s.id === resizingSelection.id);
      if (selection) {
        if (resizingSelection.edge === "left") {
          const newStartTick = Math.min(tick, selection.endTick - TICK_RATE);
          const clampedTick = Math.max(newStartTick, firstRoundStart);
          handleUpdateSelectionTime(selection.id, "startTick", clampedTick);
        } else {
          const newEndTick = Math.max(tick, selection.startTick + TICK_RATE);
          const clampedTick = Math.min(newEndTick, lastRoundEnd);
          handleUpdateSelectionTime(selection.id, "endTick", clampedTick);
        }
      }
      return;
    }

    // Handle creating new selection
    if (!isSelecting || selectionStart === null) return;

    setCurrentSelection((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        startTick: Math.min(selectionStart, tick),
        endTick: Math.max(selectionStart, tick),
      };
    });
  };

  const handleTimelineMouseUp = () => {
    // Handle resize end
    if (resizingSelection) {
      setResizingSelection(null);
      return;
    }

    // Handle new selection creation
    if (
      currentSelection &&
      currentSelection.endTick > currentSelection.startTick
    ) {
      const playerName = getPlayerName(currentSelection.playerId);
      setPanelCollapsed(false);
      setSelections((prev) => [
        ...prev,
        {
          id: `selection-${Date.now()}`,
          startTick: currentSelection.startTick,
          endTick: currentSelection.endTick,
          playerId: currentSelection.playerId,
          playerIds: [currentSelection.playerId],
          playerName,
          label: `${playerName} - ${tickToTime(currentSelection.startTick)}`,
        },
      ]);
      analytics.track("Timeline Editor - Segment Added", {
        segmentType: "manual",
      });
    }
    setIsSelecting(false);
    setSelectionStart(null);
    setCurrentSelection(null);
  };

  const handleResizeStart = (
    e: React.MouseEvent,
    selectionId: string,
    edge: "left" | "right",
  ) => {
    e.stopPropagation();
    const selection = selections.find((s) => s.id === selectionId);
    if (selection) {
      setResizingSelection({
        id: selectionId,
        edge,
        originalTick: edge === "left" ? selection.startTick : selection.endTick,
      });
    }
  };

  const handleDeleteSelection = (id: string) => {
    setSelectionsHistory((prev) => [...prev, selections]);
    setSelections((prev) => prev.filter((s) => s.id !== id));
    if (selectedSegmentId === id) {
      setSelectedSegmentId(null);
    }
    // If this selection came from a suggested moment, uncheck it
    const momentId = selectionToMomentMap.get(id);
    if (momentId) {
      setAddedMomentIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(momentId);
        return newSet;
      });
      setSelectionToMomentMap((prevMap) => {
        const newMap = new Map(prevMap);
        newMap.delete(id);
        return newMap;
      });
    }
  };

  const removeMomentFromSegment = (
    selectionId: string,
    kill: IKillEvent,
    isVictim: boolean = false,
  ) => {
    const momentKey = `${isVictim ? "death" : "kill"}-${kill.tick}-${
      kill.killer
    }-${kill.victim}`;

    setExcludedMoments((prev) => {
      const currentExcluded = prev[selectionId] || new Set<string>();
      const newExcluded = new Set(currentExcluded);
      newExcluded.add(momentKey);
      return {
        ...prev,
        [selectionId]: newExcluded,
      };
    });

    setToast(`${isVictim ? "Death" : "Kill"} removed from segment`);
  };

  const canAdjustTime = (
    selection: ISelection,
    field: "startTick" | "endTick",
    delta: number,
  ): boolean => {
    const newValue =
      field === "startTick"
        ? selection.startTick + delta
        : selection.endTick + delta;

    if (field === "startTick") {
      if (newValue >= selection.endTick) return false;
      if (newValue < firstRoundStart) return false;
      const duration = selection.endTick - newValue;
      if (duration < TICK_RATE) return false;
    } else {
      if (newValue <= selection.startTick) return false;
      if (newValue > lastRoundEnd) return false;
      const duration = newValue - selection.startTick;
      if (duration < TICK_RATE) return false;
    }

    return true;
  };

  const handleUpdateSelectionTime = (
    id: string,
    field: "startTick" | "endTick",
    value: number,
  ) => {
    setSelections((prev) => {
      const selection = prev.find((s) => s.id === id);
      if (!selection) return prev;

      const updated = { ...selection, [field]: value };
      if (updated.startTick >= updated.endTick) return prev;

      const duration = updated.endTick - updated.startTick;
      if (duration < TICK_RATE) return prev;

      const overlapping = prev.find(
        (s) =>
          s.id !== id &&
          updated.startTick < s.endTick &&
          updated.endTick > s.startTick,
      );

      if (overlapping) {
        const combinedPlayerIds = [
          ...new Set([
            ...(selection.playerIds || [selection.playerId]),
            ...(overlapping.playerIds || [overlapping.playerId]),
          ]),
        ];
        const mergedPlayerNames = combinedPlayerIds
          .map((pid) => getPlayerName(pid))
          .join(", ");
        const merged: ISelection = {
          id: `merged-${Date.now()}`,
          startTick: Math.min(updated.startTick, overlapping.startTick),
          endTick: Math.max(updated.endTick, overlapping.endTick),
          playerId: selection.playerId,
          playerIds: combinedPlayerIds,
          playerName: mergedPlayerNames,
          label: `Merged clip (${combinedPlayerIds.length} players)`,
        };
        setToast("Clips merged automatically");
        return prev
          .filter((s) => s.id !== id && s.id !== overlapping.id)
          .concat(merged);
      }

      const kills = getKillsInRange(
        updated.startTick,
        updated.endTick,
        selection.playerId,
      );

      if (kills.length > 0) {
        const firstKill = kills[0];
        const newLeadUp = Math.round(
          (firstKill.tick - updated.startTick) / TICK_RATE,
        );
        const newAftermath = Math.round(
          (updated.endTick - firstKill.tick) / TICK_RATE,
        );

        if (field === "startTick" && newLeadUp > 0) {
          setDefaultLeadUp(newLeadUp);
        } else if (field === "endTick" && newAftermath > 0) {
          setDefaultAftermath(newAftermath);
        }
      }

      return prev.map((s) => (s.id === id ? updated : s));
    });
  };

  const handleToggleJumpCuts = (selectionId: string) => {
    setSelections((prev) =>
      prev.map((s) => {
        if (s.id !== selectionId) return s;
        const newUseJumpCuts = !s.useJumpCuts;

        // Initialize kill timings if enabling jump cuts and not already set
        if (newUseJumpCuts && !s.killTimings) {
          const playerIds = s.playerIds || [s.playerId];
          const allKills: IKillEvent[] = [];
          playerIds.forEach((pid) => {
            const kills = getKillsInRange(s.startTick, s.endTick, pid);
            kills.forEach((k) => allKills.push(k));
          });
          const sortedKills = allKills.sort((a, b) => a.tick - b.tick);

          const defaultLeadUpTicks = 5 * TICK_RATE; // 5 seconds
          const defaultAftermathTicks = 2 * TICK_RATE; // 2 seconds

          const killTimings: IKillTiming[] = sortedKills.map((kill) => ({
            killKey: `${kill.tick}-${kill.killer}-${kill.victim}`,
            tick: kill.tick,
            leadUpTicks: defaultLeadUpTicks,
            aftermathTicks: defaultAftermathTicks,
          }));

          return { ...s, useJumpCuts: newUseJumpCuts, killTimings };
        }

        return { ...s, useJumpCuts: newUseJumpCuts };
      }),
    );
  };

  const handleUpdateKillTiming = (
    selectionId: string,
    killKey: string,
    field: "leadUpTicks" | "aftermathTicks",
    delta: number,
  ) => {
    setSelections((prev) =>
      prev.map((s) => {
        if (s.id !== selectionId || !s.killTimings) return s;

        const updatedTimings = s.killTimings.map((kt) => {
          if (kt.killKey !== killKey) return kt;
          const newValue = Math.max(TICK_RATE, kt[field] + delta);
          return { ...kt, [field]: newValue };
        });

        return { ...s, killTimings: updatedTimings };
      }),
    );
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollPosition(e.currentTarget.scrollLeft);
  };

  // Custom scrollbar calculations
  const totalScrollWidth = timelineWidth + PLAYER_INFO_WIDTH;
  const maxScroll = Math.max(0, totalScrollWidth - containerWidth);
  const scrollbarThumbWidth =
    containerWidth > 0
      ? Math.max(40, (containerWidth / totalScrollWidth) * 100)
      : 40;
  const scrollbarThumbPosition =
    maxScroll > 0
      ? (scrollPosition / maxScroll) * (100 - scrollbarThumbWidth)
      : 0;

  const handleScrollbarMouseDown = (e: React.MouseEvent) => {
    if (!scrollbarTrackRef.current || !containerRef.current) return;
    e.preventDefault();

    const trackRect = scrollbarTrackRef.current.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const trackWidth = trackRect.width;
    const thumbWidthPx = (scrollbarThumbWidth / 100) * trackWidth;
    const thumbLeft = (scrollbarThumbPosition / 100) * trackWidth;

    // Check if clicking on the thumb or the track
    if (clickX >= thumbLeft && clickX <= thumbLeft + thumbWidthPx) {
      // Clicking on thumb - start dragging
      setIsDraggingScrollbar(true);
      scrollbarDragStartRef.current = {
        x: e.clientX,
        scrollLeft: scrollPosition,
      };
    } else {
      // Clicking on track - jump to position
      const clickPercent = clickX / trackWidth;
      const newScrollLeft =
        clickPercent * totalScrollWidth - containerWidth / 2;
      containerRef.current.scrollTo({
        left: Math.max(0, Math.min(maxScroll, newScrollLeft)),
        behavior: "smooth",
      });
    }
  };

  const handleScrollbarMouseMove = useCallback(
    (e: MouseEvent) => {
      if (
        !isDraggingScrollbar ||
        !scrollbarDragStartRef.current ||
        !containerRef.current ||
        !scrollbarTrackRef.current
      )
        return;

      const trackRect = scrollbarTrackRef.current.getBoundingClientRect();
      const trackWidth = trackRect.width;
      const deltaX = e.clientX - scrollbarDragStartRef.current.x;
      const scrollDelta = (deltaX / trackWidth) * totalScrollWidth;
      const newScrollLeft =
        scrollbarDragStartRef.current.scrollLeft + scrollDelta;

      containerRef.current.scrollLeft = Math.max(
        0,
        Math.min(maxScroll, newScrollLeft),
      );
    },
    [isDraggingScrollbar, totalScrollWidth, maxScroll],
  );

  const handleScrollbarMouseUp = useCallback(() => {
    setIsDraggingScrollbar(false);
    scrollbarDragStartRef.current = null;
  }, []);

  // Add/remove global mouse listeners for scrollbar dragging
  useEffect(() => {
    if (isDraggingScrollbar) {
      window.addEventListener("mousemove", handleScrollbarMouseMove);
      window.addEventListener("mouseup", handleScrollbarMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleScrollbarMouseMove);
        window.removeEventListener("mouseup", handleScrollbarMouseUp);
      };
    }
  }, [isDraggingScrollbar, handleScrollbarMouseMove, handleScrollbarMouseUp]);

  const scrollToRound = (round: IRoundData) => {
    if (containerRef.current) {
      const scrollX = tickToPixel(round.startTick);
      containerRef.current.scrollTo({
        left: scrollX,
        behavior: "smooth",
      });
    }
  };

  const scrollTimelineToSegment = useCallback(
    (selection: ISelection) => {
      if (!containerRef.current) return;

      // Calculate the pixel position of the segment center
      const segmentStartX = tickToPixel(selection.startTick);
      const segmentEndX = tickToPixel(selection.endTick);
      const segmentCenterX = (segmentStartX + segmentEndX) / 2;

      // Scroll to center the segment in the viewport
      const width = containerRef.current.clientWidth;
      const scrollTarget = Math.max(0, segmentCenterX - width / 2);

      // Update scroll position state immediately for round highlighting
      setScrollPosition(scrollTarget);

      containerRef.current.scrollTo({
        left: scrollTarget,
        behavior: "smooth",
      });
    },
    [tickToPixel],
  );

  const calculateKillPreview = (kill: IKillEvent, playerId: string) => {
    const leadUpTicks = defaultLeadUp * TICK_RATE;
    const aftermathTicks = defaultAftermath * TICK_RATE;
    const newStart = Math.max(kill.tick - leadUpTicks, firstRoundStart);
    const newEnd = Math.min(kill.tick + aftermathTicks, lastRoundEnd);

    const overlapping = selections.find(
      (s) => newStart < s.endTick && newEnd > s.startTick,
    );

    return {
      startTick: newStart,
      endTick: newEnd,
      playerId,
      wouldMergeWith: overlapping?.id || null,
    };
  };

  const handleKillMarkerHover = (kill: IKillEvent, playerId: string) => {
    const preview = calculateKillPreview(kill, playerId);
    setHoveredKillPreview(preview);
  };

  const handleKillMarkerLeave = () => {
    setHoveredKillPreview(null);
  };

  const addKillMoment = (
    kill: IKillEvent,
    playerId: string,
    forceNewSegment: boolean = false,
  ) => {
    const leadUpTicks = defaultLeadUp * TICK_RATE;
    const aftermathTicks = defaultAftermath * TICK_RATE;
    const playerName = getPlayerName(playerId);
    const newStart = Math.max(kill.tick - leadUpTicks, firstRoundStart);
    const newEnd = Math.min(kill.tick + aftermathTicks, lastRoundEnd);

    setPanelCollapsed(false);
    setSelections((prev) => {
      // Skip merge check if forceNewSegment is true (Shift+click)
      if (!forceNewSegment) {
        const overlapping = prev.find(
          (s) => newStart < s.endTick && newEnd > s.startTick,
        );

        if (overlapping) {
          const mergedPlayerIds = overlapping.playerIds.includes(playerId)
            ? overlapping.playerIds
            : [...overlapping.playerIds, playerId];
          const mergedPlayerNames = mergedPlayerIds
            .map((id) => getPlayerName(id))
            .join(", ");
          const merged: ISelection = {
            id: `merged-${Date.now()}`,
            startTick: Math.min(newStart, overlapping.startTick),
            endTick: Math.max(newEnd, overlapping.endTick),
            playerId: overlapping.playerId,
            playerIds: mergedPlayerIds,
            playerName: mergedPlayerNames,
            label: `Merged clip (${mergedPlayerIds.length} players)`,
          };
          setToast("Clips merged automatically");
          return prev.filter((s) => s.id !== overlapping.id).concat(merged);
        }
      }

      analytics.track("Timeline Editor - Segment Added", {
        segmentType: "kill",
      });

      return [
        ...prev,
        {
          id: `kill-${kill.tick}-${Date.now()}`,
          startTick: newStart,
          endTick: newEnd,
          playerId,
          playerIds: [playerId],
          playerName,
          label: `${playerName} - Kill at ${tickToTime(kill.tick)}`,
        },
      ];
    });
  };

  const addDeathMoment = (
    kill: IKillEvent,
    victimId: string,
    forceNewSegment: boolean = false,
  ) => {
    const leadUpTicks = defaultLeadUp * TICK_RATE;
    const aftermathTicks = defaultAftermath * TICK_RATE;
    const playerName = getPlayerName(victimId);
    const newStart = Math.max(kill.tick - leadUpTicks, firstRoundStart);
    const newEnd = Math.min(kill.tick + aftermathTicks, lastRoundEnd);

    const newSelectionId = `death-${kill.tick}-${Date.now()}`;
    const povKey = `${newSelectionId}-${kill.tick}-${kill.killer}-${kill.victim}`;

    setPanelCollapsed(false);
    setSelections((prev) => {
      // Skip merge check if forceNewSegment is true (Shift+click)
      if (!forceNewSegment) {
        const overlapping = prev.find(
          (s) => newStart < s.endTick && newEnd > s.startTick,
        );

        if (overlapping) {
          const mergedPlayerIds = overlapping.playerIds.includes(victimId)
            ? overlapping.playerIds
            : [...overlapping.playerIds, victimId];
          const mergedPlayerNames = mergedPlayerIds
            .map((id) => getPlayerName(id))
            .join(", ");
          const merged: ISelection = {
            id: `merged-${Date.now()}`,
            startTick: Math.min(newStart, overlapping.startTick),
            endTick: Math.max(newEnd, overlapping.endTick),
            playerId: overlapping.playerId,
            playerIds: mergedPlayerIds,
            playerName: mergedPlayerNames,
            label: `Merged clip (${mergedPlayerIds.length} players)`,
          };
          setToast("Clips merged automatically");
          return prev.filter((s) => s.id !== overlapping.id).concat(merged);
        }
      }

      setKillPovPrefs((p) => ({
        ...p,
        [povKey]: "victim",
      }));

      analytics.track("Timeline Editor - Segment Added", {
        segmentType: "death",
      });

      return [
        ...prev,
        {
          id: newSelectionId,
          startTick: newStart,
          endTick: newEnd,
          playerId: victimId,
          playerIds: [victimId],
          playerName,
          label: `${playerName} - Death at ${tickToTime(kill.tick)}`,
        },
      ];
    });
  };

  const addSuggestedMoment = (moment: ISuggestedMoment) => {
    // Check if already added
    if (addedMomentIds.has(moment.id)) return;

    // Find the round this moment belongs to for boundary clamping
    const round = validRounds.find((r) => r.round === moment.round);
    const roundStart = round?.startTick ?? firstRoundStart;
    const roundEnd = round?.endTick ?? lastRoundEnd;

    const leadUpTicks = defaultLeadUp * TICK_RATE;
    const aftermathTicks = defaultAftermath * TICK_RATE;
    // Clamp to round boundaries to avoid including kills from other rounds
    const newStart = Math.max(moment.startTick - leadUpTicks, roundStart);
    const newEnd = Math.min(moment.endTick + aftermathTicks, roundEnd);

    setPanelCollapsed(false);
    setAddedMomentIds((prev) => new Set(prev).add(moment.id));

    // Check for overlapping segments before updating state
    const overlapping = selections.find(
      (s) => newStart < s.endTick && newEnd > s.startTick,
    );

    if (overlapping) {
      const mergedPlayerIds = overlapping.playerIds.includes(moment.playerId)
        ? overlapping.playerIds
        : [...overlapping.playerIds, moment.playerId];
      const mergedPlayerNames = mergedPlayerIds
        .map((id) => getPlayerName(id))
        .join(", ");
      const mergedId = `merged-${Date.now()}`;
      const merged: ISelection = {
        id: mergedId,
        startTick: Math.min(newStart, overlapping.startTick),
        endTick: Math.max(newEnd, overlapping.endTick),
        playerId: overlapping.playerId,
        playerIds: mergedPlayerIds,
        playerName: mergedPlayerNames,
        label: `Merged clip (${mergedPlayerIds.length} players)`,
      };

      // Update the map: remove old mapping, add new merged mapping
      setSelectionToMomentMap((prevMap) => {
        const newMap = new Map(prevMap);
        // Remove the old selection's mapping
        newMap.delete(overlapping.id);
        // Map the merged selection to the new moment
        newMap.set(mergedId, moment.id);
        return newMap;
      });

      setSelections((prev) =>
        prev.filter((s) => s.id !== overlapping.id).concat(merged),
      );

      // Expand and scroll to the merged segment
      setExpandedSegmentId(mergedId);
      setSelectedSegmentId(mergedId);
      setSegmentTab(mergedId, "overview");
      setTimeout(() => {
        scrollTimelineToSegment(merged);
      }, 50);

      setToast("Clips merged automatically");
      return;
    }

    analytics.track("Timeline Editor - Segment Added", {
      segmentType: moment.type,
      momentLabel: moment.label,
    });

    const newSelectionId = `${moment.type}-${moment.startTick}-${Date.now()}`;
    const newSelection: ISelection = {
      id: newSelectionId,
      startTick: newStart,
      endTick: newEnd,
      playerId: moment.playerId,
      playerIds: [moment.playerId],
      playerName: moment.playerName,
      label: `${moment.label} - ${moment.playerName} (R${moment.round})`,
    };

    setRecentlyAddedId(newSelectionId);
    setExpandedSegmentId(newSelectionId);
    setSelectedSegmentId(newSelectionId);
    setSegmentTab(newSelectionId, "overview");

    // Track which moment this selection came from
    setSelectionToMomentMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(newSelectionId, moment.id);
      return newMap;
    });

    setSelections((prev) => [...prev, newSelection]);

    // Scroll timeline to the new segment after state updates
    setTimeout(() => {
      scrollTimelineToSegment(newSelection);
    }, 50);

    setToast(`Added ${moment.label} by ${moment.playerName}`);
  };

  const getPlayerKillsInRound = (steamId: string, round: IRoundData) => {
    const kills = round.kills?.filter((k) => k.killer === steamId) || [];
    if (weaponFilter === "all") return kills;
    return kills.filter((k) => k.killerData?.weapon === weaponFilter);
  };

  const getPlayerDeathsInRound = (steamId: string, round: IRoundData) => {
    return round.kills?.filter((k) => k.victim === steamId) || [];
  };

  const getKillsInRangeAsVictim = useCallback(
    (startTick: number, endTick: number, playerId: string): IKillEvent[] => {
      const kills: IKillEvent[] = [];
      ROUNDS.forEach((round) => {
        round.kills?.forEach((kill) => {
          if (kill.tick >= startTick && kill.tick <= endTick) {
            if (kill.victim === playerId) {
              kills.push(kill);
            }
          }
        });
      });
      return kills;
    },
    [ROUNDS],
  );

  const isKillInSegment = useCallback(
    (kill: IKillEvent, playerId: string): boolean => {
      return selections.some((selection) => {
        const playerIds = selection.playerIds || [selection.playerId];
        if (!playerIds.includes(playerId)) return false;
        if (kill.tick < selection.startTick || kill.tick > selection.endTick)
          return false;

        // Check if this moment has been excluded from this segment
        const segmentExcluded = excludedMoments[selection.id];
        if (segmentExcluded) {
          const isVictim = kill.victim === playerId;
          const momentKey = `${isVictim ? "death" : "kill"}-${kill.tick}-${
            kill.killer
          }-${kill.victim}`;
          if (segmentExcluded.has(momentKey)) return false;
        }

        return true;
      });
    },
    [selections, excludedMoments],
  );

  const findSegmentContainingKill = useCallback(
    (kill: IKillEvent, playerId: string): string | null => {
      const segment = selections.find((selection) => {
        const playerIds = selection.playerIds || [selection.playerId];
        if (!playerIds.includes(playerId)) return false;
        if (kill.tick < selection.startTick || kill.tick > selection.endTick)
          return false;

        // Check if this moment has been excluded from this segment
        const segmentExcluded = excludedMoments[selection.id];
        if (segmentExcluded) {
          const isVictim = kill.victim === playerId;
          const momentKey = `${isVictim ? "death" : "kill"}-${kill.tick}-${
            kill.killer
          }-${kill.victim}`;
          if (segmentExcluded.has(momentKey)) return false;
        }

        return true;
      });
      return segment?.id || null;
    },
    [selections, excludedMoments],
  );

  const scrollToSegment = useCallback((segmentId: string) => {
    const cardElement = selectionCardRefs.current.get(segmentId);
    if (cardElement && selectionsListRef.current) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      setSelectedSegmentId(segmentId);
      setPanelCollapsed(false);
    }
  }, []);

  const getKillPovPlayer = (
    selection: ISelection,
    kill: IKillEvent,
  ): string => {
    const povKey = `${selection.id}-${kill.tick}-${kill.killer}-${kill.victim}`;
    const playerIds = selection.playerIds || [selection.playerId];
    const isVictimKill = playerIds.includes(kill.victim);
    const isKillerKill = playerIds.includes(kill.killer);
    const defaultPov = isVictimKill && !isKillerKill ? "victim" : "killer";
    const pov = killPovPrefs[povKey] || defaultPov;
    return pov === "killer" ? kill.killer : kill.victim;
  };

  const generateReplaySequence = (): IReplayCommand[] => {
    const sequence: IReplayCommand[] = [];
    let lastTick = 0;

    sequence.push({
      tick: 0,
      class: "execCommand",
      command: "cl_draw_only_deathnotices 1",
    });

    sortedSelections.forEach((selection) => {
      const playerIds = selection.playerIds || [selection.playerId];

      // Handle jump cuts mode - record each kill segment separately
      // Uses ACTUAL demo ticks for all commands so backend knows when to execute them
      if (
        selection.useJumpCuts &&
        selection.killTimings &&
        selection.killTimings.length > 0
      ) {
        // Pre-calculate all segment boundaries to detect collisions
        const allSegments = selection.killTimings
          .map((kt) => {
            const segmentStart = Math.max(
              selection.startTick,
              kt.tick - kt.leadUpTicks,
            );
            const segmentEnd = Math.min(
              selection.endTick,
              kt.tick + kt.aftermathTicks,
            );
            return {
              killKey: kt.killKey,
              start: segmentStart,
              end: segmentEnd,
            };
          })
          .sort((a, b) => a.start - b.start);

        selection.killTimings.forEach((kt) => {
          // Find the actual kill event for this timing
          let killEvent: IKillEvent | undefined;
          for (const pid of playerIds) {
            const kills = getKillsInRange(
              selection.startTick,
              selection.endTick,
              pid,
            );
            killEvent = kills.find(
              (k) => `${k.tick}-${k.killer}-${k.victim}` === kt.killKey,
            );
            if (killEvent) break;
          }

          if (!killEvent) return;

          const killPovPlayer = getKillPovPlayer(selection, killEvent);
          const playerSlot = getPlayerObserverSlot(killPovPlayer);

          // Calculate the segment boundaries for this kill (demo ticks)
          const segmentStart = Math.max(
            selection.startTick,
            kt.tick - kt.leadUpTicks,
          );
          const segmentEnd = Math.min(
            selection.endTick,
            kt.tick + kt.aftermathTicks,
          );

          // The gotoTick is where we jump to (with buffer for playback to catch up)
          const gotoTick = segmentStart - BUFFER_TICKS;

          // Jump to this kill's position
          // Use lastTick for ordering between selections, gotoTick for actual jump target
          sequence.push({
            tick: lastTick,
            class: "gotoTickAndSpectatePlayer",
            playerSlot,
            gotoTick,
            steamId64: killPovPlayer,
          });

          // Check for mounted camera on this kill
          const camKey = `${selection.id}-${killEvent.tick}-${killEvent.killer}-${killEvent.victim}-cam`;
          const mountedCam = mountedCameras[camKey];

          // Calculate camera end tick (1/4 of segment length from start)
          const segmentLength = segmentEnd - segmentStart;
          let camEndTick = Math.round(segmentStart + segmentLength / 4);

          // Check for colliding segments - if another segment starts during this camera,
          // end the camera before that segment starts (matching backend logic)
          const collidingSegment = allSegments.find(
            (seg) =>
              seg.killKey !== kt.killKey &&
              seg.start < camEndTick &&
              seg.start > segmentStart,
          );
          if (collidingSegment) {
            camEndTick = collidingSegment.start - TICK_RATE;
          }

          if (
            mountedCam &&
            mountedCam !== "none" &&
            camEndTick > segmentStart + 10
          ) {
            // Camera at beginning of segment
            sequence.push({
              tick: segmentStart,
              class: "execCommand",
              command: mountedCam,
            });
            sequence.push({
              tick: segmentStart,
              class: "startRecord",
            });
            sequence.push({
              tick: camEndTick - 2,
              class: "endRecord",
            });
            sequence.push({
              tick: camEndTick - 1,
              class: "execCommand",
              command: "as_cam_off",
            });

            // Record remainder of segment after camera ends
            if (camEndTick < segmentEnd) {
              sequence.push({
                tick: camEndTick,
                class: "startRecord",
              });
              sequence.push({
                tick: segmentEnd,
                class: "endRecord",
              });
            }
          } else {
            // No mounted camera - record the full segment
            sequence.push({
              tick: segmentStart,
              class: "startRecord",
            });
            sequence.push({
              tick: segmentEnd,
              class: "endRecord",
            });
          }

          // Update lastTick for next segment's goto ordering
          lastTick = segmentEnd + 64;
        });

        return; // Skip the normal processing for this selection
      }

      const allKillsSet = new Set<string>();
      const allKillsList: IKillEvent[] = [];

      playerIds.forEach((pid) => {
        const killsAsKiller = getKillsInRange(
          selection.startTick,
          selection.endTick,
          pid,
        );
        const killsAsVictim = getKillsInRangeAsVictim(
          selection.startTick,
          selection.endTick,
          pid,
        );
        [...killsAsKiller, ...killsAsVictim].forEach((kill) => {
          const killKey = `${kill.tick}-${kill.killer}-${kill.victim}`;
          if (!allKillsSet.has(killKey)) {
            allKillsSet.add(killKey);
            allKillsList.push(kill);
          }
        });
      });

      const allKills = allKillsList.sort((a, b) => a.tick - b.tick);

      const initialPovPlayer =
        allKills.length > 0
          ? getKillPovPlayer(selection, allKills[0])
          : selection.playerId;
      const playerSlot = getPlayerObserverSlot(initialPovPlayer);
      const gotoTick = selection.startTick - BUFFER_TICKS;

      sequence.push({
        tick: lastTick,
        class: "gotoTickAndSpectatePlayer",
        playerSlot,
        gotoTick,
        steamId64: initialPovPlayer,
      });

      let currentPovPlayer = initialPovPlayer;
      let currentRecordStart = selection.startTick;
      let recordingStarted = false;

      const povSwitches: { tick: number; steamId64: string }[] = [];

      allKills.forEach((kill, index) => {
        const killPovPlayer = getKillPovPlayer(selection, kill);

        if (index > 0 && killPovPlayer !== currentPovPlayer) {
          const switchTick = Math.max(
            allKills[index - 1].tick + TICK_RATE,
            kill.tick - TICK_RATE * 2,
          );
          povSwitches.push({
            tick: switchTick,
            steamId64: killPovPlayer,
          });
          currentPovPlayer = killPovPlayer;
        }
      });

      currentPovPlayer = initialPovPlayer;

      allKills.forEach((kill) => {
        const camKey = `${selection.id}-${kill.tick}-${kill.killer}-${kill.victim}-cam`;
        const mountedCam = mountedCameras[camKey];
        const killPovPlayer = getKillPovPlayer(selection, kill);

        const povSwitchesBeforeKill = povSwitches.filter(
          (ps) =>
            ps.tick >= currentRecordStart &&
            ps.tick < kill.tick - 7 &&
            ps.steamId64 === killPovPlayer,
        );

        if (mountedCam && mountedCam !== "none") {
          const camStartTick = kill.tick - CAMERA_LEAD_TICKS;

          // Only record the pre-camera segment if there's time before the camera starts
          if (currentRecordStart < camStartTick) {
            if (!recordingStarted) {
              sequence.push({
                tick: currentRecordStart,
                class: "startRecord",
              });
              recordingStarted = true;
            }

            povSwitchesBeforeKill.forEach((ps) => {
              sequence.push({
                tick: ps.tick,
                class: "spectatePlayer",
                steamId64: ps.steamId64,
              });
            });

            sequence.push({
              tick: camStartTick,
              class: "endRecord",
            });
            recordingStarted = false;
          }

          sequence.push({
            tick: camStartTick,
            class: "execCommand",
            command: mountedCam,
          });

          sequence.push({
            tick: kill.tick - 2,
            class: "startRecord",
          });
          recordingStarted = true;

          const camEndTick = Math.min(
            kill.tick + TICK_RATE * 2,
            selection.endTick - 2,
          );
          sequence.push({
            tick: camEndTick,
            class: "endRecord",
          });
          recordingStarted = false;

          sequence.push({
            tick: camEndTick + 1,
            class: "execCommand",
            command: "as_cam_off",
          });

          currentRecordStart = camEndTick + 2;
        } else {
          povSwitchesBeforeKill.forEach((ps) => {
            if (!recordingStarted) {
              sequence.push({
                tick: currentRecordStart,
                class: "startRecord",
              });
              recordingStarted = true;
            }
            sequence.push({
              tick: ps.tick,
              class: "spectatePlayer",
              steamId64: ps.steamId64,
            });
          });
        }
      });

      const remainingPovSwitches = povSwitches.filter(
        (ps) => ps.tick >= currentRecordStart && ps.tick < selection.endTick,
      );

      if (!recordingStarted && currentRecordStart < selection.endTick) {
        sequence.push({
          tick: currentRecordStart,
          class: "startRecord",
        });
        recordingStarted = true;
      }

      remainingPovSwitches.forEach((ps) => {
        if (
          !sequence.some(
            (cmd) =>
              cmd.class === "spectatePlayer" &&
              cmd.tick === ps.tick &&
              cmd.steamId64 === ps.steamId64,
          )
        ) {
          sequence.push({
            tick: ps.tick,
            class: "spectatePlayer",
            steamId64: ps.steamId64,
          });
        }
      });

      if (recordingStarted) {
        sequence.push({
          tick: selection.endTick,
          class: "endRecord",
        });
      } else if (currentRecordStart < selection.endTick) {
        sequence.push({
          tick: currentRecordStart,
          class: "startRecord",
        });
        sequence.push({
          tick: selection.endTick,
          class: "endRecord",
        });
      }

      lastTick = selection.endTick + 64;
    });

    // Sort sequence by tick, with secondary ordering for commands at the same tick
    // Note: jumpCutsCommands are NOT sorted here - they're appended after
    sequence.sort((a, b) => {
      if (a.tick !== b.tick) return a.tick - b.tick;
      // At the same tick, order: endRecord < execCommand < startRecord < spectatePlayer
      const classOrder: Record<string, number> = {
        endRecord: 0,
        execCommand: 1,
        startRecord: 2,
        spectatePlayer: 3,
        gotoTickAndSpectatePlayer: 4,
      };
      return (classOrder[a.class] ?? 5) - (classOrder[b.class] ?? 5);
    });

    // Post-process to fix overlapping recording segments
    // Remove redundant startRecord/endRecord commands to ensure proper alternation
    const cleanedSequence: IReplayCommand[] = [];
    let isRecording = false;

    for (const cmd of sequence) {
      if (cmd.class === "startRecord") {
        if (!isRecording) {
          cleanedSequence.push(cmd);
          isRecording = true;
        }
        // Skip redundant startRecord if already recording
      } else if (cmd.class === "endRecord") {
        if (isRecording) {
          cleanedSequence.push(cmd);
          isRecording = false;
        }
        // Skip redundant endRecord if not recording
      } else if (cmd.class === "gotoTickAndSpectatePlayer" && isRecording) {
        // During recording, convert goto to spectatePlayer (can't time-jump while recording)
        cleanedSequence.push({
          tick: cmd.tick,
          class: "spectatePlayer",
          steamId64: cmd.steamId64,
        });
      } else {
        cleanedSequence.push(cmd);
      }
    }

    return cleanedSequence;
  };

  const handleCreateVideo = () => {
    if (!hasProEditAccess) {
      analytics.track("Timeline Editor - Paywall Shown", {
        location: "timeline_editor",
        segmentCount: selections.length,
      });
      setSubscriptionPaywallOpen(true);
      return;
    }
    setShowTitleModal(true);
  };

  const handleSubmitClip = async () => {
    if (showOverhaul && isAtClipLimit(allstarUser?.user?.clipQuota)) {
      setEligibilityModalOpen(true);
      return;
    }

    const sequence = generateReplaySequence();

    if (isDebugMode) {
      setDebugSequence(sequence);
      setShowDebugModal(true);
      return;
    }

    await submitClipWithSequence(sequence);
  };

  const submitClipWithSequence = async (sequence: IReplayCommand[]) => {
    if (onGenerateSequence) {
      onGenerateSequence(sequence);
    }

    if (onSubmitSequence) {
      const result = await onSubmitSequence(
        sequence,
        clipTitle.trim() || undefined,
        { segmentCount: selections.length },
      );
      if (result.success) {
        setShowTitleModal(false);
        setShowDebugModal(false);
        setClipSubmitted(true);
        setCooldownEndTime(Date.now() + COOLDOWN_SECONDS * 1000);
      } else {
        setToast(result.errors[0]?.message || "Failed to create clip");
      }
    }
  };

  const handleDebugConfirm = () => {
    submitClipWithSequence(debugSequence);
  };

  const handleDebugCancel = () => {
    setShowDebugModal(false);
    setDebugSequence([]);
  };

  const renderTooltip = () => {
    if (!hoveredEvent) return null;

    let content;
    if (hoveredEvent.type === "kill") {
      const kill = hoveredEvent.data as IKillEvent;
      const weaponName = kill.killerData?.weapon || "Unknown";
      const killType = kill.killerData?.headShot ? "Headshot" : "Kill";
      content = (
        <>
          <TooltipTitle>
            {weaponName} {killType}
          </TooltipTitle>
          <TooltipRow>
            <span>Killer:</span> {getPlayerName(kill.killer)}
          </TooltipRow>
          <TooltipRow>
            <span>Victim:</span> {getPlayerName(kill.victim)}
          </TooltipRow>
          <TooltipRow>
            <span>Location:</span> {kill.location}
          </TooltipRow>
          <TooltipRow>
            <span>Time:</span> {tickToTime(kill.tick)}
          </TooltipRow>
          {hoveredKillPreview?.wouldMergeWith ? (
            <>
              <TooltipMerge>Will merge with existing segment</TooltipMerge>
              <TooltipAction>Shift+click for separate segment</TooltipAction>
            </>
          ) : (
            <TooltipAction>Click to add this moment</TooltipAction>
          )}
        </>
      );
    } else if (hoveredEvent.type === "death") {
      const death = hoveredEvent.data as IKillEvent;
      content = (
        <>
          <TooltipTitle>Death</TooltipTitle>
          <TooltipRow>
            <span>Killed by:</span> {getPlayerName(death.killer)}
          </TooltipRow>
          <TooltipRow>
            <span>Weapon:</span> {death.killerData?.weapon}
          </TooltipRow>
          <TooltipRow>
            <span>Location:</span> {death.location}
          </TooltipRow>
          <TooltipRow>
            <span>Time:</span> {tickToTime(death.tick)}
          </TooltipRow>
          <TooltipAction>Click to add from victim POV</TooltipAction>
          <TooltipAction>Shift+click for separate segment</TooltipAction>
        </>
      );
    } else if (hoveredEvent.type === "bomb") {
      const bomb = hoveredEvent.data as IBombEvent;
      content = (
        <>
          <TooltipTitle>
            {bomb.type === "PLANTED" ? "Bomb Planted" : "Bomb Defused"}
          </TooltipTitle>
          <TooltipRow>
            <span>Site:</span> {bomb.site}
          </TooltipRow>
          <TooltipRow>
            <span>Time:</span> {tickToTime(bomb.tick)}
          </TooltipRow>
        </>
      );
    }

    return (
      <Tooltip style={{ left: hoveredEvent.x + 10, top: hoveredEvent.y - 10 }}>
        {content}
      </Tooltip>
    );
  };

  const handleDismissWelcome = () => {
    localStorage.setItem("timelineEditorWelcomeSeen", "true");
    setShowWelcomeModal(false);
  };

  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const modKey = isMac ? "⌘" : "Ctrl";

  const renderWelcomeModal = () => {
    if (!showWelcomeModal) return null;

    return (
      <WelcomeModalOverlay onClick={handleDismissWelcome}>
        <WelcomeModalContent onClick={(e) => e.stopPropagation()}>
          <WelcomeModalTitle>Welcome to the Timeline Editor</WelcomeModalTitle>
          <WelcomeModalSubtitle>
            <FaVideo size={14} />
            Select moments, customize POV, generate clips
          </WelcomeModalSubtitle>

          <WelcomeModalSection>
            <WelcomeModalSectionTitle>How It Works</WelcomeModalSectionTitle>
            <WelcomeModalList>
              <WelcomeModalListItem>
                <WelcomeModalListNumber>1</WelcomeModalListNumber>
                <span>
                  Click on any <strong>kill marker</strong> (green pill) to add
                  that moment to your clip
                </span>
              </WelcomeModalListItem>
              <WelcomeModalListItem>
                <WelcomeModalListNumber>2</WelcomeModalListNumber>
                <span>
                  Moments that overlap will automatically merge into one segment
                </span>
              </WelcomeModalListItem>
              <WelcomeModalListItem>
                <WelcomeModalListNumber>3</WelcomeModalListNumber>
                <span>
                  Customize POV and camera angles for each kill in the sidebar
                </span>
              </WelcomeModalListItem>
            </WelcomeModalList>

            <WelcomeModalShortcuts>
              <WelcomeModalShortcutItem>
                <WelcomeModalKeyboard>Shift</WelcomeModalKeyboard>
                <span>+ click for separate segments</span>
              </WelcomeModalShortcutItem>
              <WelcomeModalShortcutItem>
                <WelcomeModalKeyboard>Delete</WelcomeModalKeyboard>
                <span>Remove selected segment</span>
              </WelcomeModalShortcutItem>
              <WelcomeModalShortcutItem>
                <WelcomeModalKeyboard>{modKey}+Z</WelcomeModalKeyboard>
                <span>Undo last action</span>
              </WelcomeModalShortcutItem>
            </WelcomeModalShortcuts>
          </WelcomeModalSection>

          <WelcomeModalWarning>
            <FaExclamationTriangle size={16} />
            <span>
              This feature is in early access and may not work as expected. Some
              clips may fail to generate or have unexpected results. We're
              actively improving it based on your feedback.
            </span>
          </WelcomeModalWarning>

          <WelcomeModalDiscord
            href="https://discord.gg/playsharestar"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaDiscord size={20} />
            <span>
              Join our Discord to share feedback and help shape this feature!
            </span>
          </WelcomeModalDiscord>

          <WelcomeModalButton onClick={handleDismissWelcome}>
            Get Started
          </WelcomeModalButton>
        </WelcomeModalContent>
      </WelcomeModalOverlay>
    );
  };

  // Get suggested moments for the wizard-selected player
  const wizardPlayerMoments = useMemo(() => {
    if (!wizardSelectedPlayer) return [];
    return suggestedMoments.filter((m) => m.playerId === wizardSelectedPlayer);
  }, [suggestedMoments, wizardSelectedPlayer]);

  const handleWizardSelectPlayer = (playerId: string) => {
    setWizardSelectedPlayer(playerId);
    // Add player to existing selection instead of replacing
    setSelectedPlayers((prev) => new Set([...prev, playerId]));
    // Set this player as the highlight focus
    setHighlightFocusPlayerId(playerId);
    // Show the moments step
    setWizardStep("moments");
  };

  // Toggle a player in the selected set
  const togglePlayerSelection = (playerId: string) => {
    setSelectedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  // Add a player to the selection
  const addPlayerToSelection = (playerId: string) => {
    setSelectedPlayers((prev) => new Set([...prev, playerId]));
    // Set this player as the highlight focus
    setHighlightFocusPlayerId(playerId);
    setShowPlayerPicker(false);
  };

  const handleWizardAddMoment = (moment: ISuggestedMoment) => {
    if (addedMomentIds.has(moment.id)) {
      // Find the selection that corresponds to this moment and remove it
      let selectionIdToRemove: string | null = null;
      selectionToMomentMap.forEach((momentId, selectionId) => {
        if (momentId === moment.id) {
          selectionIdToRemove = selectionId;
        }
      });
      if (selectionIdToRemove) {
        handleDeleteSelection(selectionIdToRemove);
      } else {
        // Moment was added but selection not found (e.g., was merged) - just clear the flag
        setAddedMomentIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(moment.id);
          return newSet;
        });
      }
      return;
    }
    addSuggestedMoment(moment);
  };

  const handleWizardComplete = () => {
    setWizardStep("editor");
    // Dismiss welcome modal if showing
    if (showWelcomeModal) {
      handleDismissWelcome();
    }
    // Scroll to a segment from the wizard player (prefer one just added)
    setTimeout(() => {
      if (selections.length > 0) {
        // Find a selection that belongs to one of the wizard player's moments
        const wizardMomentIds = new Set(wizardPlayerMoments.map((m) => m.id));
        const wizardPlayerSelection = selections.find((s) => {
          const momentId = selectionToMomentMap.get(s.id);
          return momentId && wizardMomentIds.has(momentId);
        });

        const targetSelection = wizardPlayerSelection || selections[0];
        scrollTimelineToSegment(targetSelection);
        setSelectedSegmentId(targetSelection.id);
        setExpandedSegmentId(targetSelection.id);
        setSegmentTab(targetSelection.id, "overview");
      }
    }, 100);
  };

  return (
    <Container>
      <PlayerSelectorWizard
        wizardStep={wizardStep}
        setWizardStep={setWizardStep}
        sortedPlayers={sortedPlayers}
        ctPlayerCount={ctPlayerCount}
        currentUserSteamId={currentUserSteamId}
        selectedPlayers={selectedPlayers}
        wizardSelectedPlayer={wizardSelectedPlayer}
        playerStats={playerStats}
        getPlayerName={getPlayerName}
        handleWizardSelectPlayer={handleWizardSelectPlayer}
        wizardPlayerMoments={wizardPlayerMoments}
        addedMomentIds={addedMomentIds}
        handleWizardAddMoment={handleWizardAddMoment}
        handleWizardComplete={handleWizardComplete}
        tickRate={TICK_RATE}
      />
      <HeaderWrapper $bgImage={getMapThumbnail(data.header.mapName)}>
        <Header>
          <Flex alignItems="center" style={{ gap: "12px" }}>
            <ABrandSymbol
              style={{ width: "28px", height: "28px", color: "#4dbd94" }}
            />
            <Text fontSize="18px" fontWeight="500" color="#f7fcfd">
              Timeline Editor
            </Text>
            <Text
              fontSize="10px"
              fontWeight="600"
              color="#e8a93e"
              style={{
                background: "rgba(232, 169, 62, 0.15)",
                padding: "2px 8px",
                borderRadius: "4px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Early Access
            </Text>
          </Flex>
          <Flex alignItems="center" style={{ gap: "24px" }}>
            <Flex flexDirection="column" style={{ gap: "2px" }}>
              <Text
                fontSize="11px"
                color="#557077"
                style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
              >
                Map
              </Text>
              <Text fontSize="14px" color="#8fa9af">
                {data.header.mapName.replace("de_", "").toUpperCase()}
              </Text>
            </Flex>
            <Flex flexDirection="column" style={{ gap: "2px" }}>
              <Text
                fontSize="11px"
                color="#557077"
                style={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
              >
                Length
              </Text>
              <Text fontSize="14px" color="#8fa9af">
                {tickToTime(matchDuration)}
              </Text>
            </Flex>
            <Flex alignItems="center" style={{ gap: "8px" }}>
              <ZoomButton
                as="a"
                href="https://discord.gg/playsharestar"
                target="_blank"
                rel="noopener noreferrer"
                title="Join Discord"
                style={{ width: "32px", height: "32px" }}
              >
                <FaDiscord size={14} />
              </ZoomButton>
              <ZoomButton
                onClick={() => setShowWelcomeModal(true)}
                title="Help"
                style={{ width: "32px", height: "32px" }}
              >
                <FaQuestionCircle size={14} />
              </ZoomButton>
            </Flex>
          </Flex>
        </Header>
        <ControlBar $inWrapper>
          <Flex alignItems="center" style={{ gap: "16px" }}>
            <Flex alignItems="center" style={{ gap: "8px" }}>
              <Text fontSize="12px" color="#557077">
                Players ({selectedPlayers.size}):
              </Text>
              {selectedPlayers.size > 0 && (
                <ZoomButton
                  onClick={() => setSelectedPlayers(new Set())}
                  title="Clear all selected players"
                  style={{ marginRight: "4px" }}
                >
                  Clear
                </ZoomButton>
              )}
            </Flex>
            <Flex alignItems="center" style={{ gap: "8px" }}>
              <Text fontSize="12px" color="#557077">
                Weapon:
              </Text>
              <WeaponSelect
                value={weaponFilter}
                onChange={(e) => setWeaponFilter(e.target.value)}
              >
                <option value="all">All Weapons</option>
                {uniqueWeapons.map((weapon) => (
                  <option key={weapon} value={weapon}>
                    {weapon}
                  </option>
                ))}
              </WeaponSelect>
            </Flex>
            <Flex alignItems="center" style={{ gap: "8px" }}>
              <Text fontSize="12px" color="#557077">
                Zoom:
              </Text>
              <ZoomButton
                onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
              >
                <FaMinus size={10} />
              </ZoomButton>
              <Text
                fontSize="12px"
                color="#8fa9af"
                style={{ minWidth: "40px", textAlign: "center" }}
              >
                {Math.round(zoom * 100)}%
              </Text>
              <ZoomButton onClick={() => setZoom((z) => Math.min(4, z + 0.25))}>
                <FaPlus size={10} />
              </ZoomButton>
            </Flex>
            <Flex
              alignItems="center"
              style={{ gap: "8px", marginLeft: "16px" }}
            >
              <ToggleLabel>
                <ToggleCheckbox
                  type="checkbox"
                  checked={showDeaths}
                  onChange={(e) => setShowDeaths(e.target.checked)}
                />
                <ToggleSlider />
                <Text fontSize="12px" color="#8fa9af">
                  Show Deaths
                </Text>
              </ToggleLabel>
            </Flex>
          </Flex>
        </ControlBar>
      </HeaderWrapper>

      {/* Suggested Moments Panel - show moments for focused player */}
      {selectedPlayers.size > 0 && (
        <SuggestedMomentsPanel
          collapsed={suggestionsCollapsed}
          onToggleCollapsed={() =>
            setSuggestionsCollapsed(!suggestionsCollapsed)
          }
          highlightFocusPlayerId={highlightFocusPlayerId}
          getPlayerName={getPlayerName}
          suggestedMoments={suggestedMoments}
          addedMomentIds={addedMomentIds}
          players={PLAYERS}
          addSuggestedMoment={addSuggestedMoment}
        />
      )}

      {/* Show simple welcome screen when no players selected */}
      {selectedPlayers.size === 0 ? (
        <MainContent>
          <WelcomeScreen>
            <WelcomeCard>
              <WelcomeIcon>
                <MdMovieEdit size={32} />
              </WelcomeIcon>
              <WelcomeTitle>Welcome to the Timeline Editor</WelcomeTitle>
              <WelcomeSubtitle>
                Select a player from the match to start picking their best
                moments and create clips from them!
              </WelcomeSubtitle>
              <WelcomeSelectButton onClick={() => setWizardStep("player")}>
                <FaPlus size={14} />
                Select a player
              </WelcomeSelectButton>
            </WelcomeCard>
          </WelcomeScreen>
        </MainContent>
      ) : (
        <MainContent>
          <TimelinePanel>
            <MinimapContainer>
              <MinimapLabel>Round Navigation</MinimapLabel>
              <RoundIndicator>
                {validRounds.map((round) => {
                  const showHalftime = round.round === 12;

                  return (
                    <React.Fragment key={round.round}>
                      <RoundPip
                        active={visibleRoundNumbers.has(round.round)}
                        onClick={() => scrollToRound(round)}
                        title={`Round ${round.round}`}
                      >
                        <span className="round-num">{round.round}</span>
                      </RoundPip>
                      {showHalftime && <HalftimeDivider />}
                    </React.Fragment>
                  );
                })}
              </RoundIndicator>
            </MinimapContainer>
            <TimelineContainerWrapper>
              <TimelineContainer ref={containerRef} onScroll={handleScroll}>
                <TimelineInner
                  ref={timelineRef}
                  style={{ width: timelineWidth + PLAYER_INFO_WIDTH }}
                >
                  <TimelineRuler>
                    <RulerHeader>
                      <PlayerColumnHeader></PlayerColumnHeader>
                      <RulerRow>
                        {Array.from({
                          length: Math.ceil(matchDuration / TICK_RATE / 30) + 1,
                        }).map((_, i) => {
                          const tick = firstRoundStart + i * 30 * TICK_RATE;
                          if (tick > lastRoundEnd) return null;
                          return (
                            <RulerMark
                              key={i}
                              style={{ left: tickToPixel(tick) }}
                            >
                              <span>{tickToTime(tick - firstRoundStart)}</span>
                            </RulerMark>
                          );
                        })}
                      </RulerRow>
                    </RulerHeader>
                    <RoundsHeader>
                      <PlayerColumnHeader></PlayerColumnHeader>
                      <RoundsRow>
                        {validRounds.map((round) => {
                          const startX = tickToPixel(round.startTick);
                          const width = tickToPixel(round.endTick) - startX;

                          return (
                            <RoundBlock
                              key={round.round}
                              style={{ left: startX, width }}
                            >
                              <RoundLabel>
                                Round {round.round}
                                <RoundScore>
                                  <span className="ct">
                                    {round.counterTerroristsScore}
                                  </span>
                                  <span className="separator">-</span>
                                  <span className="t">
                                    {round.terroristsScore}
                                  </span>
                                </RoundScore>
                              </RoundLabel>
                            </RoundBlock>
                          );
                        })}
                      </RoundsRow>
                    </RoundsHeader>
                  </TimelineRuler>

                  <PlayersContainer>
                    {/* Add Player row when not all players are selected and wizard is closed */}
                    {selectedPlayers.size < sortedPlayers.length &&
                      wizardStep === "editor" && (
                        <PlayerRow
                          selected={false}
                          onClick={() => setWizardStep("player")}
                          team="ct"
                          style={{ opacity: 0.7, cursor: "pointer" }}
                        >
                          <PlayerInfo
                            selected={false}
                            isCurrentUser={false}
                            team="ct"
                          >
                            <PlayerAvatar
                              selected={false}
                              isCurrentUser={false}
                              team="ct"
                            >
                              <FaPlus size={12} />
                            </PlayerAvatar>
                            <PlayerDetails>
                              <PlayerName
                                selected={false}
                                isCurrentUser={false}
                              >
                                Add Player
                              </PlayerName>
                            </PlayerDetails>
                          </PlayerInfo>
                          <PlayerTimeline selected={false}>
                            {/* Empty timeline for add row */}
                          </PlayerTimeline>
                        </PlayerRow>
                      )}
                    {/* When players are selected, show those players' timelines */}
                    {/* Show selected players' timelines */}
                    {sortedPlayers
                      .filter((p) => selectedPlayers.has(p.steamId))
                      .map((player, index) => {
                        const isSelected = selectedPlayers.has(player.steamId);
                        const stats = playerStats[player.steamId];
                        const isCurrentUser =
                          currentUserSteamId === player.steamId;
                        const originalIndex = sortedPlayers.findIndex(
                          (p) => p.steamId === player.steamId,
                        );
                        const isCTPlayer = originalIndex < ctPlayerCount;
                        const isFirstTPlayer =
                          selectedPlayers.size === 0 &&
                          index === ctPlayerCount &&
                          ctPlayerCount > 0;

                        return (
                          <React.Fragment key={player.steamId}>
                            {isFirstTPlayer && (
                              <TeamDivider>
                                <TeamDividerLine />
                              </TeamDivider>
                            )}
                            <PlayerRow
                              selected={isSelected}
                              $highlightFocused={
                                highlightFocusPlayerId === player.steamId
                              }
                              onClick={() =>
                                setHighlightFocusPlayerId(
                                  highlightFocusPlayerId === player.steamId
                                    ? null
                                    : player.steamId,
                                )
                              }
                              team={isCTPlayer ? "ct" : "t"}
                            >
                              <PlayerInfo
                                selected={isSelected}
                                isCurrentUser={isCurrentUser}
                                team={isCTPlayer ? "ct" : "t"}
                              >
                                {player.avatar ? (
                                  <PlayerAvatarImage
                                    src={player.avatar}
                                    selected={isSelected}
                                    isCurrentUser={isCurrentUser}
                                    team={isCTPlayer ? "ct" : "t"}
                                  />
                                ) : (
                                  <PlayerAvatar
                                    selected={isSelected}
                                    isCurrentUser={isCurrentUser}
                                    team={isCTPlayer ? "ct" : "t"}
                                  >
                                    <FaUser size={12} />
                                  </PlayerAvatar>
                                )}
                                <PlayerDetails>
                                  <PlayerName
                                    selected={isSelected}
                                    isCurrentUser={isCurrentUser}
                                  >
                                    {getPlayerName(player.steamId)}
                                    {isCurrentUser && (
                                      <YouBadge>(YOU)</YouBadge>
                                    )}
                                  </PlayerName>
                                  <PlayerStats>
                                    <StatBadge type="kills">
                                      <FaSkull size={8} /> {stats?.kills || 0}
                                    </StatBadge>
                                    <StatBadge type="deaths">
                                      <FaCrosshairs size={8} />{" "}
                                      {stats?.deaths || 0}
                                    </StatBadge>
                                  </PlayerStats>
                                </PlayerDetails>
                                <PlayerRemoveHint
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePlayerSelection(player.steamId);
                                    if (
                                      highlightFocusPlayerId === player.steamId
                                    ) {
                                      setHighlightFocusPlayerId(null);
                                    }
                                  }}
                                  title="Remove player"
                                >
                                  <FaMinusCircle size={12} />
                                </PlayerRemoveHint>
                              </PlayerInfo>

                              <PlayerTimeline
                                selected={isSelected}
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  handleTimelineMouseDown(e, player.steamId);
                                }}
                                onMouseMove={handleTimelineMouseMove}
                                onMouseUp={handleTimelineMouseUp}
                                onMouseLeave={handleTimelineMouseUp}
                              >
                                {validRounds.map((round) => {
                                  const startX = tickToPixel(round.startTick);
                                  const width =
                                    tickToPixel(round.endTick) - startX;
                                  const team = getPlayerTeamInRound(
                                    player.steamId,
                                    round,
                                  );

                                  return (
                                    <RoundSegment
                                      key={`${player.steamId}-${round.round}`}
                                      style={{ left: startX, width }}
                                      team={team}
                                      selected={isSelected}
                                    />
                                  );
                                })}

                                {validRounds.map((round) => {
                                  const kills = getPlayerKillsInRound(
                                    player.steamId,
                                    round,
                                  );
                                  const deaths = getPlayerDeathsInRound(
                                    player.steamId,
                                    round,
                                  );

                                  return (
                                    <React.Fragment
                                      key={`events-${player.steamId}-${round.round}`}
                                    >
                                      {kills.map((kill) => (
                                        <KillMarker
                                          key={`kill-${kill.tick}`}
                                          style={{
                                            left: tickToPixel(kill.tick),
                                          }}
                                          isHeadshot={kill.killerData?.headShot}
                                          selected={isSelected}
                                          inSegment={isKillInSegment(
                                            kill,
                                            player.steamId,
                                          )}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const existingSegmentId =
                                              findSegmentContainingKill(
                                                kill,
                                                player.steamId,
                                              );
                                            if (
                                              existingSegmentId &&
                                              !e.shiftKey
                                            ) {
                                              scrollToSegment(
                                                existingSegmentId,
                                              );
                                            } else {
                                              addKillMoment(
                                                kill,
                                                player.steamId,
                                                e.shiftKey,
                                              );
                                            }
                                          }}
                                          onMouseEnter={(e) => {
                                            setHoveredEvent({
                                              type: "kill",
                                              data: kill,
                                              x: e.clientX,
                                              y: e.clientY,
                                            });
                                            handleKillMarkerHover(
                                              kill,
                                              player.steamId,
                                            );
                                          }}
                                          onMouseLeave={() => {
                                            setHoveredEvent(null);
                                            handleKillMarkerLeave();
                                          }}
                                        >
                                          {kill.killerData?.headShot ? (
                                            <FaCrosshairs size={10} />
                                          ) : (
                                            <FaSkull size={10} />
                                          )}
                                        </KillMarker>
                                      ))}
                                      {showDeaths &&
                                        deaths.map((death) => (
                                          <DeathMarker
                                            key={`death-${death.tick}`}
                                            style={{
                                              left: tickToPixel(death.tick),
                                            }}
                                            selected={isSelected}
                                            inSegment={isKillInSegment(
                                              death,
                                              player.steamId,
                                            )}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const existingSegmentId =
                                                findSegmentContainingKill(
                                                  death,
                                                  player.steamId,
                                                );
                                              if (
                                                existingSegmentId &&
                                                !e.shiftKey
                                              ) {
                                                scrollToSegment(
                                                  existingSegmentId,
                                                );
                                              } else {
                                                addDeathMoment(
                                                  death,
                                                  player.steamId,
                                                  e.shiftKey,
                                                );
                                              }
                                            }}
                                            onMouseEnter={(e) => {
                                              setHoveredEvent({
                                                type: "death",
                                                data: death,
                                                x: e.clientX,
                                                y: e.clientY,
                                              });
                                            }}
                                            onMouseLeave={() =>
                                              setHoveredEvent(null)
                                            }
                                          >
                                            <FaSkull size={10} />
                                          </DeathMarker>
                                        ))}
                                    </React.Fragment>
                                  );
                                })}

                                {selections
                                  .filter((s) => s.playerId === player.steamId)
                                  .map((selection) => {
                                    const startX = tickToPixel(
                                      selection.startTick,
                                    );
                                    const width =
                                      tickToPixel(selection.endTick) - startX;
                                    const wouldMerge =
                                      hoveredKillPreview?.wouldMergeWith ===
                                      selection.id;
                                    const hasJumpCuts =
                                      selection.useJumpCuts &&
                                      selection.killTimings &&
                                      selection.killTimings.length > 0;

                                    return (
                                      <SelectionOverlay
                                        key={selection.id}
                                        style={{ left: startX, width }}
                                        wouldMerge={wouldMerge}
                                        selected={
                                          selectedSegmentId === selection.id
                                        }
                                        hasJumpCuts={hasJumpCuts}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedSegmentId(selection.id);
                                        }}
                                      >
                                        {/* Jump cut segments */}
                                        {hasJumpCuts &&
                                          selection.killTimings?.map((kt) => {
                                            const segmentStart = Math.max(
                                              selection.startTick,
                                              kt.tick - kt.leadUpTicks,
                                            );
                                            const segmentEnd = Math.min(
                                              selection.endTick,
                                              kt.tick + kt.aftermathTicks,
                                            );
                                            const segmentStartX =
                                              tickToPixel(segmentStart) -
                                              startX;
                                            const segmentWidth =
                                              tickToPixel(segmentEnd) -
                                              tickToPixel(segmentStart);

                                            return (
                                              <JumpCutSegmentOverlay
                                                key={kt.killKey}
                                                selected={
                                                  selectedSegmentId ===
                                                  selection.id
                                                }
                                                style={{
                                                  left: segmentStartX,
                                                  width: segmentWidth,
                                                  top: 0,
                                                  height: "100%",
                                                }}
                                              />
                                            );
                                          })}
                                        <SelectionHandle
                                          className="left"
                                          onMouseDown={(e) =>
                                            handleResizeStart(
                                              e,
                                              selection.id,
                                              "left",
                                            )
                                          }
                                        />
                                        <SelectionHandle
                                          className="right"
                                          onMouseDown={(e) =>
                                            handleResizeStart(
                                              e,
                                              selection.id,
                                              "right",
                                            )
                                          }
                                        />
                                      </SelectionOverlay>
                                    );
                                  })}

                                {hoveredKillPreview &&
                                  hoveredKillPreview.playerId ===
                                    player.steamId && (
                                    <PreviewOverlay
                                      style={{
                                        left: tickToPixel(
                                          hoveredKillPreview.startTick,
                                        ),
                                        width:
                                          tickToPixel(
                                            hoveredKillPreview.endTick,
                                          ) -
                                          tickToPixel(
                                            hoveredKillPreview.startTick,
                                          ),
                                      }}
                                      wouldMerge={
                                        !!hoveredKillPreview.wouldMergeWith
                                      }
                                    />
                                  )}

                                {currentSelection &&
                                  currentSelection.playerId ===
                                    player.steamId && (
                                    <SelectionOverlay
                                      style={{
                                        left: tickToPixel(
                                          currentSelection.startTick,
                                        ),
                                        width:
                                          tickToPixel(
                                            currentSelection.endTick,
                                          ) -
                                          tickToPixel(
                                            currentSelection.startTick,
                                          ),
                                      }}
                                      isActive
                                    />
                                  )}
                              </PlayerTimeline>
                            </PlayerRow>
                          </React.Fragment>
                        );
                      })}
                  </PlayersContainer>
                </TimelineInner>
              </TimelineContainer>
            </TimelineContainerWrapper>

            {/* Custom horizontal scrollbar */}
            <CustomScrollbarContainer>
              <CustomScrollbarTrack
                ref={scrollbarTrackRef}
                onMouseDown={handleScrollbarMouseDown}
              >
                <CustomScrollbarThumb
                  $isDragging={isDraggingScrollbar}
                  style={{
                    left: `${scrollbarThumbPosition}%`,
                    width: `${scrollbarThumbWidth}%`,
                  }}
                />
              </CustomScrollbarTrack>
            </CustomScrollbarContainer>
          </TimelinePanel>

          <SelectionsPanel collapsed={panelCollapsed}>
            <PanelHeader>
              <PanelHeaderTop>
                <CollapseButton
                  onClick={() => setPanelCollapsed(!panelCollapsed)}
                >
                  {panelCollapsed ? (
                    <FaChevronLeft size={12} />
                  ) : (
                    <FaChevronRight size={12} />
                  )}
                </CollapseButton>
                {!panelCollapsed && (
                  <Text fontSize="14px" fontWeight="600" color="#f7fcfd">
                    Your Segments ({selections.length})
                  </Text>
                )}
              </PanelHeaderTop>
              {!panelCollapsed && (
                <Text fontSize="12px" color="#557077">
                  These will become your highlight video
                </Text>
              )}
            </PanelHeader>

            {!panelCollapsed && (
              <SelectionsList ref={selectionsListRef}>
                {selections.length === 0 ? (
                  <EmptyState>
                    <EmptyIcon>
                      <FaVideo size={24} />
                    </EmptyIcon>
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      color="#f7fcfd"
                      textAlign="center"
                    >
                      No clips yet
                    </Text>
                    <Text
                      fontSize="12px"
                      color="#8fa9af"
                      textAlign="center"
                      style={{ lineHeight: 1.5 }}
                    >
                      Pick a suggested highlight from above, click kill icons on
                      the timeline, or drag to select a custom range
                    </Text>
                  </EmptyState>
                ) : (
                  sortedSelections.map((selection, index) => {
                    const playerIds = selection.playerIds || [
                      selection.playerId,
                    ];

                    const killsSet = new Set<string>();
                    const killsList: IKillEvent[] = [];
                    const deathsSet = new Set<string>();
                    const deathsList: IKillEvent[] = [];

                    playerIds.forEach((pid) => {
                      getKillsInRange(
                        selection.startTick,
                        selection.endTick,
                        pid,
                      ).forEach((kill) => {
                        const key = `${kill.tick}-${kill.killer}-${kill.victim}`;
                        if (!killsSet.has(key)) {
                          killsSet.add(key);
                          killsList.push(kill);
                        }
                      });
                      getKillsInRangeAsVictim(
                        selection.startTick,
                        selection.endTick,
                        pid,
                      ).forEach((death) => {
                        const key = `${death.tick}-${death.killer}-${death.victim}`;
                        if (!deathsSet.has(key)) {
                          deathsSet.add(key);
                          deathsList.push(death);
                        }
                      });
                    });

                    const segmentExcluded =
                      excludedMoments[selection.id] || new Set<string>();
                    const kills = killsList
                      .sort((a, b) => a.tick - b.tick)
                      .filter((kill) => {
                        const momentKey = `kill-${kill.tick}-${kill.killer}-${kill.victim}`;
                        return !segmentExcluded.has(momentKey);
                      });
                    const deaths = deathsList
                      .sort((a, b) => a.tick - b.tick)
                      .filter((death) => {
                        const momentKey = `death-${death.tick}-${death.killer}-${death.victim}`;
                        return !segmentExcluded.has(momentKey);
                      });
                    const filteredDeaths = deaths.filter(
                      (death) =>
                        !kills.some(
                          (k) =>
                            k.tick === death.tick &&
                            k.killer === death.killer &&
                            k.victim === death.victim,
                        ),
                    );

                    return (
                      <SelectionCard
                        key={selection.id}
                        ref={(el) => {
                          if (el) {
                            selectionCardRefs.current.set(selection.id, el);
                          } else {
                            selectionCardRefs.current.delete(selection.id);
                          }
                        }}
                        selected={selectedSegmentId === selection.id}
                        recentlyAdded={recentlyAddedId === selection.id}
                        onClick={() => {
                          setSelectedSegmentId(selection.id);
                          if (expandedSegmentId !== selection.id) {
                            setExpandedSegmentId(selection.id);
                            scrollTimelineToSegment(selection);
                          }
                        }}
                      >
                        <ClipHeader>
                          <Flex alignItems="center" style={{ gap: "8px" }}>
                            <ClipNumber>Segment #{index + 1}</ClipNumber>
                            <Text fontSize="10px" color="#557077">
                              •
                            </Text>
                            <Text fontSize="10px" color="#557077">
                              {kills.length}K
                            </Text>
                            <Text fontSize="10px" color="#557077">
                              •
                            </Text>
                            <Text
                              fontSize="10px"
                              color="#4dbd94"
                              fontWeight="600"
                            >
                              {Math.round(
                                getSelectionDuration(selection) / TICK_RATE,
                              )}
                              s
                            </Text>
                          </Flex>
                          {expandedSegmentId === selection.id ? (
                            <SegmentDoneButton
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedSegmentId(null);
                              }}
                            >
                              <FaCheck size={9} />
                              Done
                            </SegmentDoneButton>
                          ) : (
                            <DeleteButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSelection(selection.id);
                              }}
                              style={{ marginLeft: "auto" }}
                            >
                              <FaTrash size={10} />
                            </DeleteButton>
                          )}
                        </ClipHeader>

                        {expandedSegmentId !== selection.id && (
                          <SegmentExpandHint>
                            <FaChevronDown size={10} />
                            Click to edit
                          </SegmentExpandHint>
                        )}

                        {expandedSegmentId === selection.id && (
                          <>
                            <SegmentTabs>
                              <SegmentTab
                                $active={
                                  getSegmentTab(selection.id) === "overview"
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSegmentTab(selection.id, "overview");
                                }}
                              >
                                Overview
                              </SegmentTab>
                              <SegmentTab
                                $active={
                                  getSegmentTab(selection.id) === "timing"
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSegmentTab(selection.id, "timing");
                                }}
                              >
                                Timing
                              </SegmentTab>
                              <SegmentTab
                                $active={
                                  getSegmentTab(selection.id) === "camera"
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSegmentTab(selection.id, "camera");
                                }}
                              >
                                Camera
                              </SegmentTab>
                            </SegmentTabs>

                            <SegmentTabContent>
                              {/* Overview Tab */}
                              {getSegmentTab(selection.id) === "overview" && (
                                <>
                                  <TabInstruction>
                                    Preview your clip timeline. Remove unwanted
                                    moments by clicking the minus icon.
                                  </TabInstruction>
                                  <DurationSlider>
                                    <DurationTrack>
                                      <DurationFill
                                        hasJumpCuts={
                                          selection.useJumpCuts &&
                                          selection.killTimings &&
                                          selection.killTimings.length > 0
                                        }
                                        style={{
                                          left: "0%",
                                          width: "100%",
                                        }}
                                      />
                                      {/* Jump cut segments */}
                                      {selection.useJumpCuts &&
                                        selection.killTimings?.map((kt) => {
                                          const segmentStart = Math.max(
                                            selection.startTick,
                                            kt.tick - kt.leadUpTicks,
                                          );
                                          const segmentEnd = Math.min(
                                            selection.endTick,
                                            kt.tick + kt.aftermathTicks,
                                          );
                                          const duration =
                                            selection.endTick -
                                            selection.startTick;
                                          const leftPos =
                                            ((segmentStart -
                                              selection.startTick) /
                                              duration) *
                                            100;
                                          const widthPct =
                                            ((segmentEnd - segmentStart) /
                                              duration) *
                                            100;
                                          return (
                                            <DurationJumpCutSegment
                                              key={kt.killKey}
                                              style={{
                                                left: `${leftPos}%`,
                                                width: `${widthPct}%`,
                                              }}
                                            />
                                          );
                                        })}
                                      {kills.map((kill, i) => {
                                        const pos =
                                          ((kill.tick - selection.startTick) /
                                            (selection.endTick -
                                              selection.startTick)) *
                                          100;
                                        return (
                                          <DurationKillMarker
                                            key={i}
                                            style={{ left: `${pos}%` }}
                                            title={`${kill.killerData
                                              ?.weapon} ${
                                              kill.killerData?.headShot
                                                ? "HS"
                                                : ""
                                            }`}
                                          />
                                        );
                                      })}
                                    </DurationTrack>
                                    <DurationLabels>
                                      <span>
                                        {tickToTime(
                                          selection.startTick - firstRoundStart,
                                        )}
                                      </span>
                                      <span>
                                        {tickToTime(
                                          selection.endTick - firstRoundStart,
                                        )}
                                      </span>
                                    </DurationLabels>
                                  </DurationSlider>

                                  {(kills.length > 0 || deaths.length > 0) && (
                                    <KillsList>
                                      {kills.map((kill, killIndex) => (
                                        <KillItem key={`kill-${killIndex}`}>
                                          <Flex
                                            alignItems="center"
                                            justifyContent="space-between"
                                            style={{ width: "100%" }}
                                          >
                                            <KillItemHeader style={{ flex: 1 }}>
                                              <span className="killer">
                                                {getPlayerName(kill.killer)}
                                              </span>
                                              <KillWeaponIcon
                                                src={getWeaponIconUrl(
                                                  kill.killerData?.weapon,
                                                )}
                                                alt={kill.killerData?.weapon}
                                                onError={(e) => {
                                                  e.currentTarget.style.display =
                                                    "none";
                                                }}
                                              />
                                              {kill.killerData?.headShot && (
                                                <span className="hs">HS</span>
                                              )}
                                              <span className="victim">
                                                {getPlayerName(kill.victim)}
                                              </span>
                                            </KillItemHeader>
                                            <FaMinusCircle
                                              size={10}
                                              color="#557077"
                                              style={{
                                                cursor: "pointer",
                                                flexShrink: 0,
                                                marginLeft: "8px",
                                              }}
                                              title="Remove from segment"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                removeMomentFromSegment(
                                                  selection.id,
                                                  kill,
                                                  false,
                                                );
                                              }}
                                            />
                                          </Flex>
                                        </KillItem>
                                      ))}
                                      {filteredDeaths.map(
                                        (death, deathIndex) => (
                                          <KillItem key={`death-${deathIndex}`}>
                                            <Flex
                                              alignItems="center"
                                              justifyContent="space-between"
                                              style={{ width: "100%" }}
                                            >
                                              <KillItemHeader
                                                style={{ flex: 1 }}
                                              >
                                                <span className="killer">
                                                  {getPlayerName(death.killer)}
                                                </span>
                                                <KillWeaponIcon
                                                  src={getWeaponIconUrl(
                                                    death.killerData?.weapon,
                                                  )}
                                                  alt={death.killerData?.weapon}
                                                  onError={(e) => {
                                                    e.currentTarget.style.display =
                                                      "none";
                                                  }}
                                                />
                                                <span className="victim">
                                                  {getPlayerName(death.victim)}
                                                </span>
                                              </KillItemHeader>
                                              <FaMinusCircle
                                                size={10}
                                                color="#557077"
                                                style={{
                                                  cursor: "pointer",
                                                  flexShrink: 0,
                                                  marginLeft: "8px",
                                                }}
                                                title="Remove from segment"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  removeMomentFromSegment(
                                                    selection.id,
                                                    death,
                                                    true,
                                                  );
                                                }}
                                              />
                                            </Flex>
                                          </KillItem>
                                        ),
                                      )}
                                    </KillsList>
                                  )}
                                </>
                              )}

                              {/* Timing Tab */}
                              {getSegmentTab(selection.id) === "timing" && (
                                <>
                                  <TabInstruction>
                                    Adjust when your clip starts and ends. Use
                                    the arrows or type a time directly.
                                  </TabInstruction>
                                  <DurationSlider>
                                    <DurationTrack>
                                      <DurationFill
                                        hasJumpCuts={
                                          selection.useJumpCuts &&
                                          selection.killTimings &&
                                          selection.killTimings.length > 0
                                        }
                                        style={{
                                          left: "0%",
                                          width: "100%",
                                        }}
                                      />
                                      {/* Jump cut segments */}
                                      {selection.useJumpCuts &&
                                        selection.killTimings?.map((kt) => {
                                          const segmentStart = Math.max(
                                            selection.startTick,
                                            kt.tick - kt.leadUpTicks,
                                          );
                                          const segmentEnd = Math.min(
                                            selection.endTick,
                                            kt.tick + kt.aftermathTicks,
                                          );
                                          const duration =
                                            selection.endTick -
                                            selection.startTick;
                                          const leftPos =
                                            ((segmentStart -
                                              selection.startTick) /
                                              duration) *
                                            100;
                                          const widthPct =
                                            ((segmentEnd - segmentStart) /
                                              duration) *
                                            100;
                                          return (
                                            <DurationJumpCutSegment
                                              key={kt.killKey}
                                              style={{
                                                left: `${leftPos}%`,
                                                width: `${widthPct}%`,
                                              }}
                                            />
                                          );
                                        })}
                                      {kills.map((kill, i) => {
                                        const pos =
                                          ((kill.tick - selection.startTick) /
                                            (selection.endTick -
                                              selection.startTick)) *
                                          100;
                                        return (
                                          <DurationKillMarker
                                            key={i}
                                            style={{ left: `${pos}%` }}
                                            title={`${kill.killerData
                                              ?.weapon} ${
                                              kill.killerData?.headShot
                                                ? "HS"
                                                : ""
                                            }`}
                                          />
                                        );
                                      })}
                                    </DurationTrack>
                                    <DurationLabels>
                                      <span>
                                        {tickToTime(
                                          selection.startTick - firstRoundStart,
                                        )}
                                      </span>
                                      <span>
                                        {tickToTime(
                                          selection.endTick - firstRoundStart,
                                        )}
                                      </span>
                                    </DurationLabels>
                                    {kills.length > 0 && (
                                      <DurationTimingLabels>
                                        <span>
                                          <span className="label">
                                            Lead in to kill:
                                          </span>
                                          <span className="value">
                                            {tickToTime(
                                              kills[0].tick -
                                                selection.startTick,
                                            )}
                                          </span>
                                        </span>
                                        <span>
                                          <span className="label">
                                            After kill:
                                          </span>
                                          <span className="value">
                                            {tickToTime(
                                              selection.endTick -
                                                kills[kills.length - 1].tick,
                                            )}
                                          </span>
                                        </span>
                                      </DurationTimingLabels>
                                    )}
                                    {filteredDeaths.length > 0 && (
                                      <DurationTimingLabels>
                                        <span>
                                          <span className="label">
                                            Lead in to death:
                                          </span>
                                          <span className="value">
                                            {tickToTime(
                                              filteredDeaths[0].tick -
                                                selection.startTick,
                                            )}
                                          </span>
                                        </span>
                                        <span>
                                          <span className="label">
                                            After death:
                                          </span>
                                          <span className="value">
                                            {tickToTime(
                                              selection.endTick -
                                                filteredDeaths[
                                                  filteredDeaths.length - 1
                                                ].tick,
                                            )}
                                          </span>
                                        </span>
                                      </DurationTimingLabels>
                                    )}
                                  </DurationSlider>

                                  <TimeAdjustRow>
                                    <TimeAdjustGroup>
                                      <TimeLabel>Start</TimeLabel>
                                      <TimeInputWrapper>
                                        <TimeIncrementBtn
                                          disabled={
                                            !canAdjustTime(
                                              selection,
                                              "startTick",
                                              TICK_RATE,
                                            )
                                          }
                                          onClick={() =>
                                            handleUpdateSelectionTime(
                                              selection.id,
                                              "startTick",
                                              selection.startTick + TICK_RATE,
                                            )
                                          }
                                          title="Move start later"
                                        >
                                          <FaChevronUp size={8} />
                                        </TimeIncrementBtn>
                                        <TimeInput
                                          key={`start-${selection.id}-${selection.startTick}`}
                                          type="text"
                                          defaultValue={tickToTime(
                                            selection.startTick -
                                              firstRoundStart,
                                          )}
                                          onBlur={(e) => {
                                            const tick = timeToTick(
                                              e.target.value,
                                            );
                                            if (tick !== null) {
                                              handleUpdateSelectionTime(
                                                selection.id,
                                                "startTick",
                                                tick + firstRoundStart,
                                              );
                                            } else {
                                              e.target.value = tickToTime(
                                                selection.startTick -
                                                  firstRoundStart,
                                              );
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.currentTarget.blur();
                                            }
                                          }}
                                        />
                                        <TimeIncrementBtn
                                          disabled={
                                            !canAdjustTime(
                                              selection,
                                              "startTick",
                                              -TICK_RATE,
                                            )
                                          }
                                          onClick={() =>
                                            handleUpdateSelectionTime(
                                              selection.id,
                                              "startTick",
                                              selection.startTick - TICK_RATE,
                                            )
                                          }
                                          title="Move start earlier"
                                        >
                                          <FaChevronDown size={8} />
                                        </TimeIncrementBtn>
                                      </TimeInputWrapper>
                                    </TimeAdjustGroup>
                                    <TimeAdjustGroup>
                                      <TimeLabel>End</TimeLabel>
                                      <TimeInputWrapper>
                                        <TimeIncrementBtn
                                          disabled={
                                            !canAdjustTime(
                                              selection,
                                              "endTick",
                                              TICK_RATE,
                                            )
                                          }
                                          onClick={() =>
                                            handleUpdateSelectionTime(
                                              selection.id,
                                              "endTick",
                                              selection.endTick + TICK_RATE,
                                            )
                                          }
                                          title="Move end later"
                                        >
                                          <FaChevronUp size={8} />
                                        </TimeIncrementBtn>
                                        <TimeInput
                                          key={`end-${selection.id}-${selection.endTick}`}
                                          type="text"
                                          defaultValue={tickToTime(
                                            selection.endTick - firstRoundStart,
                                          )}
                                          onBlur={(e) => {
                                            const tick = timeToTick(
                                              e.target.value,
                                            );
                                            if (tick !== null) {
                                              handleUpdateSelectionTime(
                                                selection.id,
                                                "endTick",
                                                tick + firstRoundStart,
                                              );
                                            } else {
                                              e.target.value = tickToTime(
                                                selection.endTick -
                                                  firstRoundStart,
                                              );
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.currentTarget.blur();
                                            }
                                          }}
                                        />
                                        <TimeIncrementBtn
                                          disabled={
                                            !canAdjustTime(
                                              selection,
                                              "endTick",
                                              -TICK_RATE,
                                            )
                                          }
                                          onClick={() =>
                                            handleUpdateSelectionTime(
                                              selection.id,
                                              "endTick",
                                              selection.endTick - TICK_RATE,
                                            )
                                          }
                                          title="Move end earlier"
                                        >
                                          <FaChevronDown size={8} />
                                        </TimeIncrementBtn>
                                      </TimeInputWrapper>
                                    </TimeAdjustGroup>
                                  </TimeAdjustRow>

                                  {/* Jump Cuts Section - only show if there are 2+ kills */}
                                  {kills.length >= 2 && (
                                    <JumpCutsSection>
                                      <JumpCutsToggle
                                        onClick={() =>
                                          handleToggleJumpCuts(selection.id)
                                        }
                                      >
                                        <Box>
                                          <JumpCutsLabel>
                                            Skip time between kills
                                          </JumpCutsLabel>
                                          <JumpCutsDescription>
                                            Jump directly to each kill instead
                                            of showing downtime
                                          </JumpCutsDescription>
                                        </Box>
                                        <ToggleLabel>
                                          <ToggleCheckbox
                                            type="checkbox"
                                            checked={
                                              selection.useJumpCuts || false
                                            }
                                            onChange={() => {}}
                                          />
                                          <ToggleSlider />
                                        </ToggleLabel>
                                      </JumpCutsToggle>

                                      {selection.useJumpCuts &&
                                        selection.killTimings && (
                                          <JumpCutsKillList>
                                            {selection.killTimings.map(
                                              (kt, index) => {
                                                const kill = kills.find(
                                                  (k) =>
                                                    `${k.tick}-${k.killer}-${k.victim}` ===
                                                    kt.killKey,
                                                );
                                                if (!kill) return null;

                                                return (
                                                  <JumpCutsKillItem
                                                    key={kt.killKey}
                                                  >
                                                    <JumpCutsKillNumber>
                                                      {index + 1}
                                                    </JumpCutsKillNumber>
                                                    <JumpCutsKillInfo>
                                                      <JumpCutsKillPlayer>
                                                        {getPlayerName(
                                                          kill.killer,
                                                        )}
                                                      </JumpCutsKillPlayer>
                                                      <JumpCutsKillWeapon>
                                                        {kill.killerData
                                                          ?.weapon || "Unknown"}
                                                        {kill.killerData
                                                          ?.headShot && " HS"}
                                                      </JumpCutsKillWeapon>
                                                    </JumpCutsKillInfo>
                                                    <JumpCutsTimingControls>
                                                      <JumpCutsTimingGroup>
                                                        <JumpCutsTimingLabel>
                                                          Before
                                                        </JumpCutsTimingLabel>
                                                        <JumpCutsTimingValue>
                                                          <JumpCutsTimingBtn
                                                            onClick={() =>
                                                              handleUpdateKillTiming(
                                                                selection.id,
                                                                kt.killKey,
                                                                "leadUpTicks",
                                                                -TICK_RATE,
                                                              )
                                                            }
                                                            disabled={
                                                              kt.leadUpTicks <=
                                                              TICK_RATE
                                                            }
                                                          >
                                                            <FaMinus size={8} />
                                                          </JumpCutsTimingBtn>
                                                          <JumpCutsTimingDisplay>
                                                            {Math.round(
                                                              kt.leadUpTicks /
                                                                TICK_RATE,
                                                            )}
                                                            s
                                                          </JumpCutsTimingDisplay>
                                                          <JumpCutsTimingBtn
                                                            onClick={() =>
                                                              handleUpdateKillTiming(
                                                                selection.id,
                                                                kt.killKey,
                                                                "leadUpTicks",
                                                                TICK_RATE,
                                                              )
                                                            }
                                                          >
                                                            <FaPlus size={8} />
                                                          </JumpCutsTimingBtn>
                                                        </JumpCutsTimingValue>
                                                      </JumpCutsTimingGroup>
                                                      <JumpCutsTimingGroup>
                                                        <JumpCutsTimingLabel>
                                                          After
                                                        </JumpCutsTimingLabel>
                                                        <JumpCutsTimingValue>
                                                          <JumpCutsTimingBtn
                                                            onClick={() =>
                                                              handleUpdateKillTiming(
                                                                selection.id,
                                                                kt.killKey,
                                                                "aftermathTicks",
                                                                -TICK_RATE,
                                                              )
                                                            }
                                                            disabled={
                                                              kt.aftermathTicks <=
                                                              TICK_RATE
                                                            }
                                                          >
                                                            <FaMinus size={8} />
                                                          </JumpCutsTimingBtn>
                                                          <JumpCutsTimingDisplay>
                                                            {Math.round(
                                                              kt.aftermathTicks /
                                                                TICK_RATE,
                                                            )}
                                                            s
                                                          </JumpCutsTimingDisplay>
                                                          <JumpCutsTimingBtn
                                                            onClick={() =>
                                                              handleUpdateKillTiming(
                                                                selection.id,
                                                                kt.killKey,
                                                                "aftermathTicks",
                                                                TICK_RATE,
                                                              )
                                                            }
                                                          >
                                                            <FaPlus size={8} />
                                                          </JumpCutsTimingBtn>
                                                        </JumpCutsTimingValue>
                                                      </JumpCutsTimingGroup>
                                                    </JumpCutsTimingControls>
                                                  </JumpCutsKillItem>
                                                );
                                              },
                                            )}
                                          </JumpCutsKillList>
                                        )}
                                    </JumpCutsSection>
                                  )}
                                </>
                              )}

                              {/* Camera Tab */}
                              {getSegmentTab(selection.id) === "camera" && (
                                <>
                                  <TabInstruction>
                                    Choose whose perspective to watch each kill
                                    from.
                                  </TabInstruction>
                                  {(kills.length > 0 ||
                                    filteredDeaths.length > 0) && (
                                    <CameraOptionsSection>
                                      {kills.map((kill, killIndex) => {
                                        const povKey = `${selection.id}-${kill.tick}-${kill.killer}-${kill.victim}`;
                                        const currentPov =
                                          killPovPrefs[povKey] || "killer";
                                        const { killerTeam } =
                                          getKillTeams(kill);

                                        return (
                                          <CameraKillItem
                                            key={`cam-kill-${killIndex}`}
                                          >
                                            <CameraKillInfo>
                                              <CameraKillNumber>
                                                {killIndex + 1}
                                              </CameraKillNumber>
                                              <CameraKillPlayers>
                                                <span className="killer">
                                                  {getPlayerName(kill.killer)}
                                                </span>
                                                <span className="arrow">→</span>
                                                <span className="victim">
                                                  {getPlayerName(kill.victim)}
                                                </span>
                                              </CameraKillPlayers>
                                            </CameraKillInfo>
                                            <CameraPovSwitch>
                                              <CameraPovBtn
                                                $active={
                                                  currentPov === "killer"
                                                }
                                                $team={killerTeam}
                                                onClick={() =>
                                                  setKillPovPrefs((prev) => ({
                                                    ...prev,
                                                    [povKey]: "killer",
                                                  }))
                                                }
                                                title="Watch from killer's view"
                                              >
                                                Killer
                                              </CameraPovBtn>
                                              <CameraPovBtn
                                                $active={
                                                  currentPov === "victim"
                                                }
                                                $team={
                                                  killerTeam === "ct"
                                                    ? "t"
                                                    : killerTeam === "t"
                                                    ? "ct"
                                                    : null
                                                }
                                                onClick={() =>
                                                  setKillPovPrefs((prev) => ({
                                                    ...prev,
                                                    [povKey]: "victim",
                                                  }))
                                                }
                                                title="Watch from victim's view"
                                              >
                                                Victim
                                              </CameraPovBtn>
                                            </CameraPovSwitch>
                                            {(() => {
                                              const camKey = `${selection.id}-${kill.tick}-${kill.killer}-${kill.victim}-cam`;
                                              const currentCam =
                                                mountedCameras[camKey] ||
                                                "none";
                                              return (
                                                <CameraIconButton
                                                  $hasCamera={
                                                    currentCam !== "none"
                                                  }
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    const rect =
                                                      e.currentTarget.getBoundingClientRect();
                                                    const popoverHeight = 250;
                                                    const spaceAbove = rect.top;
                                                    const spaceBelow =
                                                      window.innerHeight -
                                                      rect.bottom;
                                                    const openAbove =
                                                      spaceBelow <
                                                        popoverHeight &&
                                                      spaceAbove > spaceBelow;
                                                    setPopoverPosition({
                                                      top: openAbove
                                                        ? rect.top -
                                                          popoverHeight
                                                        : rect.bottom + 4,
                                                      left: rect.right - 120,
                                                    });
                                                    setOpenCameraPopover(
                                                      openCameraPopover ===
                                                        camKey
                                                        ? null
                                                        : camKey,
                                                    );
                                                  }}
                                                  title="Mounted camera"
                                                  data-camera-popover
                                                >
                                                  <FaVideo size={12} />
                                                </CameraIconButton>
                                              );
                                            })()}
                                            {openCameraPopover ===
                                              `${selection.id}-${kill.tick}-${kill.killer}-${kill.victim}-cam` &&
                                              popoverPosition &&
                                              createPortal(
                                                <CameraPopover
                                                  data-camera-popover
                                                  $top={popoverPosition.top}
                                                  $left={popoverPosition.left}
                                                >
                                                  {MOUNTED_CAMERA_OPTIONS.map(
                                                    (option) => (
                                                      <CameraPopoverOption
                                                        key={option.value}
                                                        $active={
                                                          (mountedCameras[
                                                            `${selection.id}-${kill.tick}-${kill.killer}-${kill.victim}-cam`
                                                          ] || "none") ===
                                                          option.value
                                                        }
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setMountedCameras(
                                                            (prev) => ({
                                                              ...prev,
                                                              [`${selection.id}-${kill.tick}-${kill.killer}-${kill.victim}-cam`]:
                                                                option.value,
                                                            }),
                                                          );
                                                          setOpenCameraPopover(
                                                            null,
                                                          );
                                                        }}
                                                      >
                                                        {option.label}
                                                      </CameraPopoverOption>
                                                    ),
                                                  )}
                                                </CameraPopover>,
                                                document.body,
                                              )}
                                          </CameraKillItem>
                                        );
                                      })}
                                      {filteredDeaths.map(
                                        (death, deathIndex) => {
                                          const povKey = `${selection.id}-${death.tick}-${death.killer}-${death.victim}`;
                                          const currentPov =
                                            killPovPrefs[povKey] || "victim";
                                          const { killerTeam } =
                                            getKillTeams(death);

                                          return (
                                            <CameraKillItem
                                              key={`cam-death-${deathIndex}`}
                                            >
                                              <CameraKillInfo>
                                                <CameraKillNumber
                                                  style={{
                                                    background:
                                                      "rgba(249, 83, 112, 0.15)",
                                                    color: "#f95370",
                                                  }}
                                                >
                                                  D{deathIndex + 1}
                                                </CameraKillNumber>
                                                <CameraKillPlayers>
                                                  <span className="killer">
                                                    {getPlayerName(
                                                      death.killer,
                                                    )}
                                                  </span>
                                                  <span className="arrow">
                                                    →
                                                  </span>
                                                  <span className="victim">
                                                    {getPlayerName(
                                                      death.victim,
                                                    )}
                                                  </span>
                                                </CameraKillPlayers>
                                              </CameraKillInfo>
                                              <CameraPovSwitch>
                                                <CameraPovBtn
                                                  $active={
                                                    currentPov === "killer"
                                                  }
                                                  $team={killerTeam}
                                                  onClick={() =>
                                                    setKillPovPrefs((prev) => ({
                                                      ...prev,
                                                      [povKey]: "killer",
                                                    }))
                                                  }
                                                  title="Watch from killer's view"
                                                >
                                                  Killer
                                                </CameraPovBtn>
                                                <CameraPovBtn
                                                  $active={
                                                    currentPov === "victim"
                                                  }
                                                  $team={
                                                    killerTeam === "ct"
                                                      ? "t"
                                                      : killerTeam === "t"
                                                      ? "ct"
                                                      : null
                                                  }
                                                  onClick={() =>
                                                    setKillPovPrefs((prev) => ({
                                                      ...prev,
                                                      [povKey]: "victim",
                                                    }))
                                                  }
                                                  title="Watch from victim's view"
                                                >
                                                  Victim
                                                </CameraPovBtn>
                                              </CameraPovSwitch>
                                              {(() => {
                                                const camKey = `${selection.id}-${death.tick}-${death.killer}-${death.victim}-cam`;
                                                const currentCam =
                                                  mountedCameras[camKey] ||
                                                  "none";
                                                return (
                                                  <CameraIconButton
                                                    $hasCamera={
                                                      currentCam !== "none"
                                                    }
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const rect =
                                                        e.currentTarget.getBoundingClientRect();
                                                      const popoverHeight = 250;
                                                      const spaceAbove =
                                                        rect.top;
                                                      const spaceBelow =
                                                        window.innerHeight -
                                                        rect.bottom;
                                                      const openAbove =
                                                        spaceBelow <
                                                          popoverHeight &&
                                                        spaceAbove > spaceBelow;
                                                      setPopoverPosition({
                                                        top: openAbove
                                                          ? rect.top -
                                                            popoverHeight
                                                          : rect.bottom + 4,
                                                        left: rect.right - 120,
                                                      });
                                                      setOpenCameraPopover(
                                                        openCameraPopover ===
                                                          camKey
                                                          ? null
                                                          : camKey,
                                                      );
                                                    }}
                                                    title="Mounted camera"
                                                    data-camera-popover
                                                  >
                                                    <FaVideo size={12} />
                                                  </CameraIconButton>
                                                );
                                              })()}
                                              {openCameraPopover ===
                                                `${selection.id}-${death.tick}-${death.killer}-${death.victim}-cam` &&
                                                popoverPosition &&
                                                createPortal(
                                                  <CameraPopover
                                                    data-camera-popover
                                                    $top={popoverPosition.top}
                                                    $left={popoverPosition.left}
                                                  >
                                                    {MOUNTED_CAMERA_OPTIONS.map(
                                                      (option) => (
                                                        <CameraPopoverOption
                                                          key={option.value}
                                                          $active={
                                                            (mountedCameras[
                                                              `${selection.id}-${death.tick}-${death.killer}-${death.victim}-cam`
                                                            ] || "none") ===
                                                            option.value
                                                          }
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            setMountedCameras(
                                                              (prev) => ({
                                                                ...prev,
                                                                [`${selection.id}-${death.tick}-${death.killer}-${death.victim}-cam`]:
                                                                  option.value,
                                                              }),
                                                            );
                                                            setOpenCameraPopover(
                                                              null,
                                                            );
                                                          }}
                                                        >
                                                          {option.label}
                                                        </CameraPopoverOption>
                                                      ),
                                                    )}
                                                  </CameraPopover>,
                                                  document.body,
                                                )}
                                            </CameraKillItem>
                                          );
                                        },
                                      )}
                                    </CameraOptionsSection>
                                  )}
                                  {kills.length === 0 &&
                                    filteredDeaths.length === 0 && (
                                      <Text
                                        fontSize="12px"
                                        color="#557077"
                                        style={{ textAlign: "center" }}
                                      >
                                        No moments to configure
                                      </Text>
                                    )}
                                </>
                              )}
                            </SegmentTabContent>
                          </>
                        )}
                      </SelectionCard>
                    );
                  })
                )}
              </SelectionsList>
            )}

            {!panelCollapsed && selections.length > 0 && (
              <PanelFooter>
                {(() => {
                  const totalDurationSeconds = Math.round(
                    selections.reduce(
                      (acc, s) => acc + getSelectionDuration(s),
                      0,
                    ) / TICK_RATE,
                  );
                  const isTooLong = totalDurationSeconds >= 120;
                  return (
                    <>
                      <TotalDuration>
                        <Text fontSize="12px" color="#557077">
                          Total video length:
                        </Text>
                        <Text
                          fontSize="14px"
                          fontWeight="600"
                          color={isTooLong ? "#f95370" : "#4dbd94"}
                        >
                          {totalDurationSeconds}s{isTooLong && " (max 120s)"}
                        </Text>
                      </TotalDuration>
                      <CreateButton
                        onClick={handleCreateVideo}
                        disabled={
                          isSubmitting || cooldownRemaining > 0 || isTooLong
                        }
                      >
                        <FaVideo size={12} />
                        <span>
                          {cooldownRemaining > 0
                            ? `Wait ${cooldownRemaining}s`
                            : "Generate Clip"}
                        </span>
                      </CreateButton>
                    </>
                  );
                })()}
              </PanelFooter>
            )}
          </SelectionsPanel>
        </MainContent>
      )}

      {renderTooltip()}

      {toast && (
        <Toast>
          <FaLayerGroup size={14} />
          <span>{toast}</span>
        </Toast>
      )}

      {renderWelcomeModal()}

      <TitleModal
        show={showTitleModal}
        onClose={() => setShowTitleModal(false)}
        clipTitle={clipTitle}
        setClipTitle={setClipTitle}
        selections={selections}
        getSelectionDuration={getSelectionDuration}
        tickRate={TICK_RATE}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmitClip}
      />

      {showDebugModal && (
        <WelcomeModalOverlay onClick={handleDebugCancel}>
          <WelcomeModalContent
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "800px", maxHeight: "80vh" }}
          >
            <WelcomeModalTitle>Debug: Replay Sequence</WelcomeModalTitle>
            <WelcomeModalSubtitle>
              {debugSequence.length} commands
            </WelcomeModalSubtitle>

            <Box
              style={{
                background: "#0d1519",
                borderRadius: "8px",
                padding: "16px",
                maxHeight: "400px",
                overflow: "auto",
                width: "100%",
                marginTop: "16px",
                fontFamily: "monospace",
                fontSize: "11px",
                lineHeight: "1.6",
              }}
            >
              <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(debugSequence, null, 2)}
              </pre>
            </Box>

            <Flex
              flexDirection="row"
              style={{ gap: "12px", width: "100%", marginTop: "20px" }}
            >
              <WelcomeModalButton
                onClick={handleDebugCancel}
                style={{ flex: 1, background: "#253a49" }}
              >
                Cancel
              </WelcomeModalButton>
              <WelcomeModalButton
                onClick={handleDebugConfirm}
                style={{ flex: 1 }}
              >
                Submit Clip
              </WelcomeModalButton>
            </Flex>
          </WelcomeModalContent>
        </WelcomeModalOverlay>
      )}

      {clipSubmitted && (
        <WelcomeModalOverlay onClick={() => setClipSubmitted(false)}>
          <WelcomeModalContent onClick={(e) => e.stopPropagation()}>
            <WelcomeModalTitle>Clip submitted!</WelcomeModalTitle>
            <WelcomeModalSubtitle>
              <FaVideo size={14} />
              Your clip is being generated
            </WelcomeModalSubtitle>

            <Text
              fontSize="14px"
              color="#8fa9af"
              textAlign="center"
              style={{ margin: "20px 0" }}
            >
              This usually takes a few minutes. You can check the status and
              view your completed clip on your profile page.
            </Text>

            <Flex flexDirection="column" style={{ gap: "12px", width: "100%" }}>
              <WelcomeModalButton
                as={profileButtonCountdown > 0 ? "button" : "a"}
                href={profileButtonCountdown > 0 ? undefined : "/profile"}
                disabled={profileButtonCountdown > 0}
                style={{
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  boxSizing: "border-box",
                  opacity: profileButtonCountdown > 0 ? 0.6 : 1,
                  cursor:
                    profileButtonCountdown > 0 ? "not-allowed" : "pointer",
                }}
              >
                {profileButtonCountdown > 0
                  ? `Go to My Profile (${profileButtonCountdown}s)`
                  : "Go to My Profile"}
              </WelcomeModalButton>
              <WelcomeModalButton
                onClick={() => setClipSubmitted(false)}
                style={{
                  background: "transparent",
                  border: "1px solid #3a4a52",
                  color: "#8fa9af",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                Continue Editing
              </WelcomeModalButton>
            </Flex>
          </WelcomeModalContent>
        </WelcomeModalOverlay>
      )}

      {/* Player Picker Modal */}
      <PlayerPickerModal
        show={showPlayerPicker}
        onClose={() => setShowPlayerPicker(false)}
        sortedPlayers={sortedPlayers}
        ctPlayerCount={ctPlayerCount}
        currentUserSteamId={currentUserSteamId}
        selectedPlayers={selectedPlayers}
        playerStats={playerStats}
        getPlayerName={getPlayerName}
        addPlayerToSelection={addPlayerToSelection}
      />
      {eligibilityModalOpen && EligibilityModalPortal}
      {subscriptionPaywallOpen && SubscriptionPaywallPortal}
    </Container>
  );
};

const TimelineEditorPage = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { data, loading, error, submitReplaySequence, createClipLoading } =
    useTimelineEditorViewModel({ matchId: matchId || "" });

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingContent>
          <LoadingTitle>Loading Timeline Editor</LoadingTitle>
          <LoadingSubtitle>
            Fetching match data and player stats
          </LoadingSubtitle>
        </LoadingContent>
      </LoadingContainer>
    );
  }

  if (error || !data) {
    return (
      <LoadingContainer>
        <Text fontSize="16px" color="#f95370">
          {error?.message || "Failed to load match data"}
        </Text>
      </LoadingContainer>
    );
  }

  return (
    <TimelineEditor
      data={data}
      onSubmitSequence={submitReplaySequence}
      isSubmitting={createClipLoading}
    />
  );
};

export { TimelineEditor, TimelineEditorPage };
